import { scopeLines } from "@/lib/scope-lines";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mark } from "@/components/mark";
import { jobMarks, workOrderMissingMark } from "@/lib/job-marks";
import { useQuietToolbar } from "@/lib/quiet-toolbar";
import { useQuery } from "@tanstack/react-query";
import {
    AlertTriangle,
    CheckCircle,
    Clock,
    Loader2,
    Lock,
    MapPin,
    CalendarOff,
    MoreVertical,
    Navigation,
    Pencil,
    UserX,
    Wrench,
    X,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { outBadgeText, type OutState } from "./out-days";
import { brandedLabel } from "@/lib/lifecycle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TierBadge } from "@/components/ui/tier-badge";
import { Popover, PopoverAnchor, PopoverClose, PopoverContent } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { JobTaskCornerTab, JobTaskFlagProvider, useJobTaskFlag } from "@/components/job-task-flag";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuLabel,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { formatFieldTime, localDateKey } from "@/lib/datetime";
import { classifyDueState, formatDuePillLabel, scheduledPastDueTitle } from "@/lib/dueUrgency";
import { isContactStale } from "@/lib/contactFreshness";
import {
    DAY_WINDOW_END_HOUR,
    DAY_WINDOW_START_HOUR,
    DEFAULT_DAILY_CAPACITY_MINUTES,
    DEFAULT_VISIT_MINUTES,
    MIN_VISIT_MINUTES,
    TRADE_HEX,
    cardDurationLabel,
    formatJobAddress,
    jobVitals,
    readableCardWidth,
    vitalsVisible,
    type BoardCardDensity,
    type DispatchJob,
    type DispatchTech,
} from "./dispatch-types";
import { useBoardDensity } from "@/lib/use-board-density";
import { assemblyName, formatAssets } from "@/lib/vocabulary";
import { loadDayScroll, saveDayScroll } from "./grid-state";
import { CapacityStrip } from "./capacity-strip";
import { HELPER_DRAG_MIME, TECH_DRAG_MIME, HelperChip, type HelperChipInfo } from "./helper-chips";
import { useHighlightedJobId } from "./highlight-job-context";
import { crewChip, crewMembers, removeCrewLabel } from "./crew";
import { leftChipText, techCounts } from "./new-grid/card-state";

export type JobPulseRisk = "ok" | "watch" | "at_risk" | "overdue";
export interface JobPulseEntry {
    risk: JobPulseRisk;
    note: string;
}

/** G3: repair/replace intent — from the calculator's breakdown when cached,
 *  else inferred from the job type so pre-refresh jobs still read right. */
export function isRepairIntentJob(job: DispatchJob): boolean {
    if (job.points_breakdown?.job_intent === "repair") return true;
    if (job.points_breakdown?.job_intent === "test") return false;
    return /repair|replace|rebuild/i.test(`${job.job_type_code ?? ""} ${job.job_type_name ?? ""}`);
}

function formatPts(n: number): string {
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function formatBlockMinutes(minutes: number): string {
    if (minutes >= 60) {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return m === 0 ? `${h}h` : `${h}h ${m}m`;
    }
    return `${minutes}m`;
}

/**
 * #1143 — what the Grid's confirm menu entry should say and do for a job.
 *
 * Exported and pure because the polarity is the part that can silently be
 * wrong: sending `confirm: true` for a job that is already confirmed is a
 * no-op the dispatcher would read as the board ignoring them. The label and
 * the value are derived from one condition here, rather than as two separate
 * ternaries in JSX that can drift apart.
 */
/**
 * Issue #1245 — who can be ADDED to a job's crew (as opposed to reassigned to).
 * Anyone already on the job — the lead or an existing crew member — is not a
 * candidate, so the menu never offers a no-op that comes back as a 409.
 */
export function addCrewCandidates(
    job: Pick<DispatchJob, "assigned_to" | "crew">,
    techs: DispatchTech[],
): DispatchTech[] {
    return techs.filter((t) => t.id !== job.assigned_to && !(job.crew ?? []).some((c) => c.user_id === t.id));
}

export function confirmMenuAction(job: Pick<DispatchJob, "dispatch_lock_state">): {
    label: string;
    nextConfirm: boolean;
} {
    const isConfirmed = job.dispatch_lock_state === "confirmed";
    return {
        label: isConfirmed ? "Unconfirm booking" : "Confirm booking",
        nextConfirm: !isConfirmed,
    };
}

/** Quick-action bundle threaded down to every job block's context menu. */
interface JobQuickActions {
    techs: DispatchTech[];
    /** Open the explicit Schedule dialog (pick tech + day + time). */
    onSchedule?: (jobId: string) => void;
    onMoveToTomorrow?: (jobId: string) => void;
    onPushNextWeek?: (jobId: string) => void;
    onReassign?: (jobId: string, techId: string) => void;
    /** Issue #1245 — put a SECOND tech on this job without moving it off the
     *  first. Job-scoped, unlike the day-scoped Helper bench pairing. */
    onAddCrew?: (jobId: string, techId: string) => void;
    /** 11/3 — take one added tech off this job; the lead and time stay. */
    onRemoveCrew?: (jobId: string, userId: string) => void;
    /** Adjust the job's scheduled start/end time in place. */
    onAdjustTime?: (jobId: string) => void;
    /** Move the job to a specific calendar day, keeping tech + wall-clock time. */
    onMoveToSpecificDate?: (jobId: string) => void;
    /** Clear the job's tech + time (back to awaiting_scheduling). */
    onUnschedule?: (jobId: string) => void;
    /** Message the assigned tech (best-effort navigation). Only surfaced
     *  when the job is assigned to a tech. */
    onMessageTech?: (jobId: string) => void;
    /** Mark the job as skipped / cannot-complete. */
    onSkipJob?: (jobId: string) => void;
    /** Cancel the job (opens a reason dialog in the shell). */
    onCancelJob?: (jobId: string) => void;
    /** #1143 — confirm / unconfirm the booking without leaving the board.
     *  The Grid already READS `dispatch_lock_state` to draw the lock pill; it
     *  just had no way to set it, so Darcy had to open the job page for every
     *  confirmation and come back. Same endpoint the job page's Booking card
     *  uses (POST / DELETE /api/jobs/:id/confirm). */
    onToggleConfirm?: (jobId: string, confirm: boolean) => void;
    /** Resulting-date labels for the relative shortcuts, computed by the
     *  shell from TODAY's real date (e.g. "Move to tomorrow (Wed Jun 17)"). */
    moveToTomorrowLabel?: string;
    pushNextWeekLabel?: string;
}

interface DaySwimlaneProps {
    techs: DispatchTech[];
    techJobMap: Map<string, DispatchJob[]>;
    /** ISO date key like 2026-04-21; the swimlane anchors on this calendar day. */
    selectedDate: string;
    onJobClick: (jobId: string) => void;
    /**
     * Pre-cutover #37 — direct "edit job details" affordance on each
     * job tile. Wires the pencil icon shown on hover. Optional so
     * existing callers stay source-compatible; when omitted the
     * pencil is hidden.
     */
    onJobEdit?: (jobId: string) => void;
    /**
     * Drop handler. Called when a draggable job (from the unscheduled rail or
     * another tech row) is released onto a tech row at a particular time. The
     * shell turns this into a PATCH on the job. `ineligibleReason` is set
     * when the dispatcher overrides a skill/city mismatch — the shell should
     * still apply the assignment but warn the user.
     */
    onDrop: (jobId: string, newTechId: string, newStart: Date, ineligibleReason?: string | null) => void;
    /** Optional jobId -> live risk metadata. Adds animated indicators. */
    pulseByJob?: Map<string, JobPulseEntry>;
    /** Optional techId -> active skill codes. Drives the small skill chips
     *  shown in the tech row label and powers eligibility highlighting. */
    techSkills?: Map<string, string[]>;
    /** Optional jobId -> skill codes required by this job's job_type. Empty
     *  or missing means "no skill restriction". Kept for the display chips. */
    jobSkillReqs?: Map<string, string[]>;
    /** Optional techId -> set of job_type_id the tech is approved to lead.
     *  This is the real lead-eligibility gate (replaces the skill check). */
    techJobTypeApprovals?: Map<string, Set<string>>;
    /** Optional jobId -> job_type_id. Missing = no job type = no lead gate. */
    jobJobTypeId?: Map<string, string>;
    /** Optional job_type_id -> short label, used in ineligibility tooltips. */
    jobTypeLabelById?: Map<string, string>;
    /** Optional techId -> set of restricted_city_id this tech is approved
     *  to work in. Used to gate drops onto restricted cities like Plano. */
    techCityCerts?: Map<string, Set<string>>;
    /** Optional jobId -> { id, label } when the job's site falls in a
     *  restricted city. Jobs not in this map have no city gate. */
    jobCityRestriction?: Map<string, { id: string; label: string }>;
    /** Optional registry of restricted cities (id -> short label like
     *  "Plano"). Still used to compute drop eligibility during drags. */
    restrictedCityLabels?: Map<string, string>;
    /** Optional leadTechId -> helper sticker chips riding with that lead
     *  today. Helpers never get their own lane — they render as draggable
     *  chips pinned to their lead's lane header (THE-GRID-NOTES §2). */
    helpersByLead?: Map<string, HelperChipInfo[]>;
    /** Id of the helper chip currently being dragged (lifted to the shell so
     *  the bench strip and the lane headers stay in sync). */
    draggingHelperId?: string | null;
    onHelperDragStart?: (helperId: string) => void;
    onHelperDragEnd?: () => void;
    /** A helper chip was dropped on a lane header. The shell opens the
     *  scope popover (just today / rest of week / make default) at (x, y). */
    onHelperDrop?: (helperId: string, targetLeadId: string, x: number, y: number) => void;
    /** One-click revert-to-default on an override chip. */
    onHelperRevert?: (helper: HelperChipInfo) => void;
    /** HELPER-MODE V3: a TECH's name was dragged onto another tech's lane —
     *  the shell opens the scope popover to pair him as acting-helper. */
    onTechDrop?: (techId: string, targetLeadId: string, x: number, y: number) => void;
    /** Optional id of the job currently being dragged. When set, tech rows
     *  the job can't be assigned to are greyed out. */
    draggingJobId?: string | null;
    /** Notified when a swimlane block starts being dragged so the shell can
     *  cascade the dragging-job state to other surfaces. */
    onJobDragStart?: (jobId: string) => void;
    /** Notified on drop / dragend so the shell can clear dragging state. */
    onJobDragEnd?: () => void;
    /** Jobs that have scheduled_start in the current day range but no
     *  assigned_to. Rendered in an "Unassigned" row at the top so
     *  dispatchers can drag them onto a tech. */
    unassignedJobs?: DispatchJob[];
    /** #826: whether the board is showing today. The Behind / On track badge
     *  measures elapsed time against plan, so on a future day it could only
     *  ever say green — it renders NOT AT ALL there instead of a verdict
     *  that can't be false. Defaults to true (today). */
    isToday?: boolean;
    /** #826 (audit C4): techId -> jobs ASSIGNED to them with NO scheduled
     *  time. A time grid can't draw a timeless job, so these were invisible
     *  while the tech's row said "No jobs scheduled". Rendered as an amber
     *  chip in the lane header on every date. */
    unslottedByTech?: Map<string, { count: number; labels: string[] }>;
    /** Quick-action: open the explicit Schedule dialog for the job. */
    onJobSchedule?: (jobId: string) => void;
    /** Quick-action: reschedule the job to tomorrow (relative to TODAY). */
    onJobMoveToTomorrow?: (jobId: string) => void;
    /** Quick-action: reschedule the job to +7 days (relative to TODAY). */
    onJobPushNextWeek?: (jobId: string) => void;
    /** Quick-action: reassign the job to another tech, keeping its time. */
    onJobReassign?: (jobId: string, techId: string) => void;
    /** Quick-action: add a second tech to the job's crew, keeping the lead. */
    onJobAddCrew?: (jobId: string, techId: string) => void;
    /** 11/3 — take one added tech off a crew job (the lead stays). */
    onJobRemoveCrew?: (jobId: string, userId: string) => void;
    /** Quick-action: adjust the job's scheduled start/end time in place. */
    onJobAdjustTime?: (jobId: string) => void;
    /** Quick-action: move the job to a specific day, keeping tech + time. */
    onJobMoveToSpecificDate?: (jobId: string) => void;
    /** Quick-action: unschedule the job (clear tech + time). */
    onJobUnschedule?: (jobId: string) => void;
    /** Quick-action: open the messaging surface for the assigned tech. */
    onJobMessageTech?: (jobId: string) => void;
    /** Quick-action: mark the job skipped / cannot-complete. */
    onJobSkip?: (jobId: string) => void;
    /** Quick-action: cancel the job (shell opens a reason dialog). */
    onJobCancel?: (jobId: string) => void;
    /** #1143 — confirm/unconfirm from the board. */
    onJobToggleConfirm?: (jobId: string, confirm: boolean) => void;
    /** Resulting-date labels for the relative shortcuts (computed by shell). */
    moveToTomorrowLabel?: string;
    pushNextWeekLabel?: string;
    /** When true, override the configured day window to a full 24h (0–24) so
     *  jobs scheduled outside business hours are visible (timeline scrolls). */
    fullDayView?: boolean;
    /** #1268 — techId -> why they're off the board on this date (PTO, a
     *  call-in, or a day the office marked out for a class). The lane still
     *  renders: a tech who is out but holds work needs to be SEEN, not
     *  hidden. */
    outByTech?: Map<string, OutState>;
    /** #1268 — open the "mark someone out" dialog seeded with this tech. */
    onMarkOut?: (techId: string) => void;
    /** Click an out badge or an out helper's chip — move or clear that day. */
    onOutClick?: (userId: string) => void;
    /** #975 "Fit day": reframe the window to the day's actual work span and
     *  compress the clock so it fits the viewport. Opt-in — the default
     *  opening position is untouched until a dispatcher asks for this. */
    fitDay?: boolean;
}

const TECH_LABEL_WIDTH = 180;
const MIN_PX_PER_MIN = 2.0;
/** Ghost card width on a roomy board; below GHOST_TAB_MAX_PX it collapses to
 *  GHOST_TAB_WIDTH so it stops covering the real card underneath (#975). */
const GHOST_WIDTH = 96;
const GHOST_TAB_WIDTH = 26;
const GHOST_TAB_MAX_PX = 900;
/** A gap shorter than this before a tech's first stop is ordinary morning
 *  slack, not a "late start" worth labelling (#975). */
const LEAD_IN_MIN_PX = 120;

/**
 * Configured dispatch day window (org_settings `dispatch.day.start_hour` /
 * `dispatch.day.end_hour`). Falls back to the historical 7–17 defaults so the
 * grid renders before settings load and when the keys are unset. Cached at
 * module scope so every swimlane shares one fetch.
 */
let dayWindowCache: { startHour: number; endHour: number } | null = null;
let dayWindowPromise: Promise<{ startHour: number; endHour: number }> | null = null;

function loadDayWindow(): Promise<{ startHour: number; endHour: number }> {
    if (dayWindowCache) return Promise.resolve(dayWindowCache);
    if (!dayWindowPromise) {
        dayWindowPromise = api
            .get<{ settings: Record<string, unknown> }>("/api/settings")
            .then((data) => {
                const s = data.settings ?? {};
                const num = (k: string, fallback: number): number => {
                    const v = s[k];
                    const n = typeof v === "number" ? v : Number(v);
                    return Number.isFinite(n) ? n : fallback;
                };
                const startHour = num("dispatch.day.start_hour", DAY_WINDOW_START_HOUR);
                const endHourRaw = num("dispatch.day.end_hour", DAY_WINDOW_END_HOUR);
                const endHour = endHourRaw > startHour ? endHourRaw : startHour + 1;
                dayWindowCache = { startHour, endHour };
                return dayWindowCache;
            })
            .catch(() => {
                dayWindowCache = { startHour: DAY_WINDOW_START_HOUR, endHour: DAY_WINDOW_END_HOUR };
                return dayWindowCache;
            });
    }
    return dayWindowPromise;
}

export function useDayWindow(): { startHour: number; endHour: number } {
    const [win, setWin] = useState(
        () => dayWindowCache ?? { startHour: DAY_WINDOW_START_HOUR, endHour: DAY_WINDOW_END_HOUR },
    );
    useEffect(() => {
        let alive = true;
        loadDayWindow().then((w) => {
            if (alive) setWin(w);
        });
        return () => {
            alive = false;
        };
    }, []);
    return win;
}

/**
 * Timeline scale + the measured width of the scroll viewport.
 *
 * `containerWidth` is the second half of the viewport primitive (#975): paired
 * with the live scrollLeft it tells each lane which of its stops are actually
 * on screen. Nothing in dispatch knew that before — the old edge chips only
 * knew about the clock *window*, which is a different (and usually wider)
 * thing than the viewport.
 *
 * `fitSpanMinutes` is "Fit day": when set, the scale is allowed below
 * MIN_PX_PER_MIN so the day's real work span can be squeezed into view.
 */
function useTimelineScale(dayMinutes: number, fitSpanMinutes?: number | null) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [pxPerMin, setPxPerMin] = useState(MIN_PX_PER_MIN);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width ?? 0;
            setContainerWidth(w);
            const available = w - TECH_LABEL_WIDTH - 16;
            if (available > 0) {
                // Fit day drops the readability floor on purpose — it is an
                // explicit "show me the whole day" request. Card widths still
                // have their own floor, so this compresses the clock, not the
                // cards (see #975 / SPEC E6).
                const floor = fitSpanMinutes && fitSpanMinutes > 0 ? 0.1 : MIN_PX_PER_MIN;
                setPxPerMin(Math.max(floor, available / dayMinutes));
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [dayMinutes, fitSpanMinutes]);

    const timelineWidth = dayMinutes * pxPerMin;
    return { containerRef, pxPerMin, timelineWidth, containerWidth };
}

function dayStartLocal(dateKey: string, startHour: number): Date {
    const [y, m, d] = dateKey.split("-").map((n) => Number.parseInt(n, 10));
    const dt = new Date(y, (m ?? 1) - 1, d ?? 1, startHour, 0, 0, 0);
    return dt;
}

function snapMinutes(minutes: number, step = 15): number {
    return Math.round(minutes / step) * step;
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

/**
 * #975 "Fit day": the hours that actually hold work, read straight off the raw
 * job times (no layout needed). Null for a day with nothing timed. Shared by
 * the classic board and the new Grid so "Fit day" means the same on both.
 */
export function workSpanFor(
    techs: Pick<DispatchTech, "id">[],
    techJobMap: Map<string, DispatchJob[]>,
): { startHour: number; endHour: number } | null {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const tech of techs) {
        for (const job of techJobMap.get(tech.id) ?? []) {
            if (!job.scheduled_start) continue;
            const startMs = Date.parse(job.scheduled_start);
            if (!Number.isFinite(startMs)) continue;
            const endRaw = job.scheduled_end ? Date.parse(job.scheduled_end) : NaN;
            const minutes = Number.isFinite(endRaw)
                ? Math.max(MIN_VISIT_MINUTES, (endRaw - startMs) / 60_000)
                : Math.max(MIN_VISIT_MINUTES, job.estimated_duration_minutes ?? DEFAULT_VISIT_MINUTES);
            const start = new Date(startMs);
            const startH = start.getHours() + start.getMinutes() / 60;
            min = Math.min(min, startH);
            max = Math.max(max, startH + minutes / 60);
        }
    }
    // A day with nothing scheduled has no span to fit — stay put.
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    const startHour = clamp(Math.floor(min), 0, 23);
    const endHour = clamp(Math.ceil(max), startHour + 1, 24);
    return { startHour, endHour };
}

/**
 * The hours a day board shows: "Fit day" wins, then "Full day (24h)" (12 am to
 * 12 am), then the org's configured window. Both boards use this.
 */
export function boardHours(
    configured: { startHour: number; endHour: number },
    opts: { fullDayView?: boolean; workSpan?: { startHour: number; endHour: number } | null },
): { startHour: number; endHour: number } {
    if (opts.workSpan) return opts.workSpan;
    if (opts.fullDayView) return { startHour: 0, endHour: 24 };
    return configured;
}

export interface PositionedBlock {
    job: DispatchJob;
    leftPx: number;
    widthPx: number;
    startMs: number;
    endMs: number;
    /** True scheduled minutes for this visit — drives the duration chip and
     *  the honest at-a-glance duration bar (width is capped, minutes are not). */
    minutes: number;
    /** True if the block bleeds before the visible window. */
    clippedLeft: boolean;
    /** True if the block bleeds after the visible window. */
    clippedRight: boolean;
    /**
     * #628 item 1: how far right of its true clock pixel this card was packed,
     * in minutes (0 = the card sits exactly at its clock position). > 0 means
     * the card abuts the previous stop in a tight sequence — the card leads
     * with a back-to-back marker so abutting stops read as sequential, never
     * simultaneous.
     */
    pushedMinutes: number;
}

interface PositionedBlocks {
    blocks: PositionedBlock[];
    /** Count of jobs scheduled entirely before the visible window. */
    beforeCount: number;
    /** Count of jobs scheduled entirely after the visible window. */
    afterCount: number;
    /** Right edge of the last packed card (px) — the lane's content width. */
    contentRight: number;
}

/** Minimum visible gap between two packed cards, in px. (#628: widened 6 → 8
 *  so abutting stops separate visibly; still inside buildDriveSegments'
 *  "packed-tight, not a real gap" suppression threshold.) */
const CARD_GAP_PX = 8;

/**
 * Lay a tech's day out as **readable, clock-anchored, collision-packed** cards
 * (#587 — the G3 doctrine amendment).
 *
 * - Each card's true duration (`minutes`, from manual-schedule-wins → model →
 *   60-min default, clamped to the 30-min floor) is preserved exactly — it
 *   drives the duration chip and tooltip, unchanged from before.
 * - Card WIDTH is `readableCardWidth(minutes, density)`: a readable floor (no
 *   more "S…" slivers) with a hard cap (no sprawl), monotonic in between so a
 *   long repair is still visibly wider than a short test.
 * - Each card sits at its real clock position, then is packed left-to-right so
 *   readable-width cards never overlap (`left = max(clockLeft, prevRight+gap)`).
 *   Busy lanes extend past the window and scroll; every card stays legible.
 */
export function buildPositionedBlocks(
    jobs: DispatchJob[],
    dayStart: Date,
    pxPerMin: number,
    windowHours: number,
    density: BoardCardDensity = "comfortable",
): PositionedBlocks {
    const dayStartMs = dayStart.getTime();
    const dayEndMs = dayStartMs + windowHours * 60 * 60_000;
    const prelim: Array<{ block: PositionedBlock; clockLeft: number }> = [];
    let beforeCount = 0;
    let afterCount = 0;
    for (const job of jobs) {
        if (!job.scheduled_start) continue;
        const startMs = Date.parse(job.scheduled_start);
        if (!Number.isFinite(startMs)) continue;
        // Minimum card = 30 minutes, period (G3 amendment) — even when the
        // stored window is shorter, the card never renders smaller.
        const endRaw = job.scheduled_end ? Date.parse(job.scheduled_end) : NaN;
        const minutes = Number.isFinite(endRaw)
            ? Math.max(MIN_VISIT_MINUTES, (endRaw - startMs) / 60_000)
            : Math.max(MIN_VISIT_MINUTES, job.estimated_duration_minutes ?? DEFAULT_VISIT_MINUTES);
        const endMs = startMs + minutes * 60_000;
        // Jobs entirely outside the window are counted (surfaced as edge
        // indicators) instead of silently dropped. Partial overlaps still
        // render clipped to the visible edge.
        if (endMs <= dayStartMs) {
            beforeCount++;
            continue;
        }
        if (startMs >= dayEndMs) {
            afterCount++;
            continue;
        }
        const clippedLeft = startMs < dayStartMs;
        const clippedRight = endMs > dayEndMs;
        const visibleStartMs = Math.max(startMs, dayStartMs);
        const clockLeft = ((visibleStartMs - dayStartMs) / 60_000) * pxPerMin;
        const widthPx = readableCardWidth(minutes, density);
        prelim.push({
            block: {
                job,
                leftPx: clockLeft,
                widthPx,
                startMs,
                endMs,
                minutes,
                clippedLeft,
                clippedRight,
                pushedMinutes: 0,
            },
            clockLeft,
        });
    }
    prelim.sort((a, b) => a.block.startMs - b.block.startMs);
    // Pack left-to-right: each card keeps its clock position unless the previous
    // (readable-width) card would overlap it, in which case push right to abut.
    let prevRight = 0;
    const blocks: PositionedBlock[] = [];
    for (const { block, clockLeft } of prelim) {
        const left = blocks.length === 0 ? clockLeft : Math.max(clockLeft, prevRight + CARD_GAP_PX);
        prevRight = left + block.widthPx;
        blocks.push({ ...block, leftPx: left, pushedMinutes: pxPerMin > 0 ? (left - clockLeft) / pxPerMin : 0 });
    }
    return { blocks, beforeCount, afterCount, contentRight: prevRight };
}

/** One side's worth of stops that are scrolled out of view (#975). */
export interface OffscreenSide {
    /** How many of this tech's stops are hidden on this side. */
    count: number;
    /** The hidden stop nearest the viewport edge — the jump target, and the
     *  one whose time the ghost shows. */
    nearest: PositionedBlock;
    /** Convenience: `nearest.startMs`. */
    nearestStartMs: number;
    /** Points hidden on this side, summed from `computed_points`. */
    points: number;
}

export interface OffscreenGhosts {
    before: OffscreenSide | null;
    after: OffscreenSide | null;
}

/**
 * Which of a lane's stops are scrolled out of the viewport — the calculation
 * behind the ghost cards and the rail's "N off screen" badge (#975).
 *
 * This is deliberately VIEWPORT-relative, which is what the pre-existing
 * `beforeCount` / `afterCount` are not: those count jobs outside the clock
 * *window*, and the window is routinely wider than the viewport (the 24-hour
 * view is 2,880px against a ~1,180px screen). In that very common case the old
 * counters are both zero while a tech's entire day sits off screen.
 *
 * Coordinates are TRACK-LOCAL. The track's x=0 sits at content
 * x=TECH_LABEL_WIDTH and the rail is sticky-left, so the visible band is
 * exactly [scrollLeft, scrollLeft + clientWidth - TECH_LABEL_WIDTH].
 *
 * `blocks` arrive sorted by start and packed left-to-right, so `leftPx` is
 * non-decreasing and the nearest hidden stop on each side is simply the last
 * of `before` / the first of `after`.
 */
export function computeOffscreenGhosts(
    blocks: PositionedBlock[],
    viewportLeftPx: number,
    viewportRightPx: number,
): OffscreenGhosts {
    // Before the ResizeObserver reports, the viewport is degenerate. Claiming
    // everything is hidden would flash a ghost onto every lane on first paint.
    if (!(viewportRightPx > viewportLeftPx)) return { before: null, after: null };

    const before: PositionedBlock[] = [];
    const after: PositionedBlock[] = [];
    for (const b of blocks) {
        if (b.leftPx + b.widthPx <= viewportLeftPx) before.push(b);
        else if (b.leftPx >= viewportRightPx) after.push(b);
    }

    const points = (list: PositionedBlock[]): number => {
        const total = list.reduce((sum, b) => {
            const n = b.job.computed_points == null ? 0 : Number(b.job.computed_points);
            return sum + (Number.isFinite(n) ? n : 0);
        }, 0);
        return Math.round(total * 10) / 10;
    };
    const side = (list: PositionedBlock[], nearest: PositionedBlock | undefined): OffscreenSide | null =>
        list.length > 0 && nearest
            ? { count: list.length, nearest, nearestStartMs: nearest.startMs, points: points(list) }
            : null;

    return {
        before: side(before, before[before.length - 1]),
        after: side(after, after[0]),
    };
}

/** How far past a card's right edge (px) a drop still reads as "right after
 *  that stop" rather than an open-space clock drop (#628 item 2). */
const DROP_AFTER_SLOP_PX = 18;

export interface DropLanding {
    /** Minutes after day start the drop actually lands at. */
    minutes: number;
    /** Track pixel where the landing guide should draw — the visual truth of
     *  where the job will go, which in a packed lane is NOT the raw cursor. */
    guidePx: number;
    /** The stop this drop lands immediately after, when it snaps to one. */
    afterJobId: string | null;
}

/**
 * #628 item 2 — drop precision in a packed lane. Packing draws cards right of
 * their clock pixel, so the raw cursor→clock mapping lands a job at a time
 * slightly off from where it visually dropped. When the cursor is ON a card
 * (or in the tight seam just after it), the dispatcher's intent is "right
 * after that stop" — land exactly at that card's end time, back-to-back, and
 * put the guide at that seam so the preview never lies. Open-space drops keep
 * the plain snapped clock mapping. Purely visual→time reconciliation: the
 * PATCH/scheduling path downstream is untouched.
 */
export function landingForDrop(
    px: number,
    blocks: PositionedBlock[],
    pxPerMin: number,
    dayStartMs: number,
): DropLanding {
    // Blocks are packed left-to-right, so lefts are non-decreasing: the anchor
    // is the last card whose left edge sits at or left of the cursor.
    let anchor: PositionedBlock | null = null;
    for (const b of blocks) {
        if (px >= b.leftPx) anchor = b;
        else break;
    }
    if (anchor && px <= anchor.leftPx + anchor.widthPx + DROP_AFTER_SLOP_PX) {
        return {
            minutes: Math.max(0, (anchor.endMs - dayStartMs) / 60_000),
            guidePx: anchor.leftPx + anchor.widthPx + CARD_GAP_PX / 2,
            afterJobId: anchor.job.id,
        };
    }
    const minutes = snapMinutes(px / pxPerMin);
    return { minutes, guidePx: minutes * pxPerMin, afterJobId: null };
}

interface DriveSegment {
    leftPx: number;
    widthPx: number;
    /** Schedule gap between the two consecutive jobs, in minutes. */
    gapMinutes: number;
    /**
     * Haversine-based estimated drive time, in minutes. `null` when
     * either consecutive job is missing site coordinates — fall back
     * to `gapMinutes` for the label in that case.
     *
     * Purely visual per PRE-CUTOVER #41 — the auto-scheduler still
     * owns the capacity strip, this never participates in the labour
     * total.
     */
    driveMinutes: number | null;
}

/**
 * Quick & dirty haversine in kilometres. Mirrors the auto-scheduler
 * (`src/server/routes/dispatchAutoSchedule.ts`) so the swimlane visual
 * agrees with the placement engine's own distance math.
 */
function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
    const R = 6371;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const t =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(t), Math.sqrt(1 - t));
}

const SWIMLANE_AVG_SPEED_KMH = 40; // matches dispatchAutoSchedule.AVG_SPEED_KMH

function estimateDriveMinutes(a: DispatchJob, b: DispatchJob): number | null {
    if (
        a.latitude == null ||
        a.longitude == null ||
        b.latitude == null ||
        b.longitude == null ||
        !Number.isFinite(a.latitude) ||
        !Number.isFinite(a.longitude) ||
        !Number.isFinite(b.latitude) ||
        !Number.isFinite(b.longitude)
    ) {
        return null;
    }
    const km = haversineKm({ lat: a.latitude, lng: a.longitude }, { lat: b.latitude, lng: b.longitude });
    return Math.max(1, Math.round((km / SWIMLANE_AVG_SPEED_KMH) * 60));
}

function buildDriveSegments(blocks: PositionedBlock[]): DriveSegment[] {
    const segments: DriveSegment[] = [];
    for (let i = 0; i < blocks.length - 1; i += 1) {
        const a = blocks[i]!;
        const b = blocks[i + 1]!;
        if (b.startMs <= a.endMs) continue;
        const leftPx = a.leftPx + a.widthPx;
        const widthPx = b.leftPx - leftPx;
        // Cards are readable-width + packed, so only draw a drive/gap bar when
        // a real visible gap opened up between them (packed-tight neighbours
        // sit ~CARD_GAP_PX apart — that's not a gap, skip it).
        if (widthPx <= 8) continue;
        segments.push({
            leftPx,
            widthPx,
            gapMinutes: Math.round((b.startMs - a.endMs) / 60_000),
            driveMinutes: estimateDriveMinutes(a.job, b.job),
        });
    }
    return segments;
}

/**
 * Returns whether a tech is approved to lead the dragged job's job_type,
 * AND (when the dragged job lives in a restricted city) holds an active
 * approval for that city. The two reasons are surfaced separately so the UI
 * can spell out exactly what's missing in the tooltip.
 */
function computeEligibility(
    techApprovedJobTypeIds: Set<string> | null,
    jobTypeId: string | null,
    jobTypeLabel: string | null,
    cityRestriction: { id: string; label: string } | null,
    techCityIds: Set<string> | null,
): { eligible: boolean; missingApprovalLabel: string | null; missingCityLabel: string | null } {
    const missingApprovalLabel =
        jobTypeId && !(techApprovedJobTypeIds?.has(jobTypeId) ?? false) ? (jobTypeLabel ?? "this job type") : null;
    const missingCityLabel =
        cityRestriction && !(techCityIds?.has(cityRestriction.id) ?? false) ? cityRestriction.label : null;
    return {
        eligible: missingApprovalLabel === null && missingCityLabel === null,
        missingApprovalLabel,
        missingCityLabel,
    };
}

function totalBookedMinutes(blocks: PositionedBlock[]): number {
    let m = 0;
    for (const b of blocks) {
        m += (b.endMs - b.startMs) / 60_000;
    }
    return Math.round(m);
}

export function DaySwimlane({
    techs,
    techJobMap,
    selectedDate,
    onJobClick,
    onJobEdit,
    onDrop,
    pulseByJob,
    techSkills,
    jobSkillReqs,
    techJobTypeApprovals,
    jobJobTypeId,
    jobTypeLabelById,
    techCityCerts,
    jobCityRestriction,
    helpersByLead,
    draggingHelperId,
    onHelperDragStart,
    onHelperDragEnd,
    onHelperDrop,
    onHelperRevert,
    onTechDrop,
    draggingJobId: externalDraggingJobId,
    onJobDragStart,
    onJobDragEnd,
    unassignedJobs,
    onJobSchedule,
    onJobMoveToTomorrow,
    onJobPushNextWeek,
    onJobReassign,
    onJobAddCrew,
    onJobRemoveCrew,
    onJobAdjustTime,
    onJobMoveToSpecificDate,
    onJobUnschedule,
    onJobMessageTech,
    onJobSkip,
    onJobCancel,
    onJobToggleConfirm,
    moveToTomorrowLabel,
    pushNextWeekLabel,
    fullDayView = false,
    isToday = true,
    unslottedByTech,
    outByTech,
    onMarkOut,
    onOutClick,
    fitDay = false,
}: DaySwimlaneProps) {
    const configuredWindow = useDayWindow();

    // #975 "Fit day". The span comes straight off the raw job times, BEFORE any
    // layout: lanes depend on pxPerMin, which would depend on the span, which
    // would depend on the lanes. Reading scheduled_start / scheduled_end breaks
    // that cycle — it needs no geometry at all.
    const workSpan = useMemo(() => (fitDay ? workSpanFor(techs, techJobMap) : null), [fitDay, techs, techJobMap]);

    const { startHour, endHour } = boardHours(configuredWindow, { fullDayView, workSpan });
    const windowHours = endHour - startHour;
    const dayMinutes = windowHours * 60;
    const hours = useMemo(
        () => Array.from({ length: windowHours + 1 }, (_, i) => startHour + i),
        [startHour, windowHours],
    );
    const dayStart = useMemo(() => dayStartLocal(selectedDate, startHour), [selectedDate, startHour]);
    const dayStartMs = dayStart.getTime();
    const {
        containerRef: swimlaneRef,
        pxPerMin,
        timelineWidth,
        containerWidth,
    } = useTimelineScale(dayMinutes, workSpan ? dayMinutes : null);
    // #587: card sizing / lane height / chrome density. Read directly from the
    // shared hook (the command bar's toggle drives it) — no prop threading.
    const { density: boardDensity } = useBoardDensity();

    // Lay out every lane up front so we can size the whole board to the widest
    // packed lane (readable-width cards can push a busy lane past the clock
    // window). `contentWidth` then drives the ruler, each track, and the outer
    // scroll min-width so the horizontal scroll reaches every card.
    const lanes = useMemo(
        () =>
            techs.map((tech) => {
                const jobs = techJobMap.get(tech.id) ?? [];
                const positioned = buildPositionedBlocks(jobs, dayStart, pxPerMin, windowHours, boardDensity);
                const bookedPoints = positioned.blocks.reduce((sum, b) => {
                    const p = b.job.computed_points;
                    const n = p == null ? 0 : Number(p);
                    return sum + (Number.isFinite(n) ? n : 0);
                }, 0);
                return {
                    tech,
                    ...positioned,
                    drives: buildDriveSegments(positioned.blocks),
                    booked: totalBookedMinutes(positioned.blocks),
                    bookedPoints,
                };
            }),
        [techs, techJobMap, dayStart, pxPerMin, windowHours, boardDensity],
    );
    const maxContentRight = lanes.reduce((m, l) => Math.max(m, l.contentRight), 0);
    const contentWidth = Math.max(timelineWidth, maxContentRight + 24);

    // #975 — the live horizontal viewport. rAF-throttled because a raw scroll
    // handler fires far faster than we can usefully re-render every lane; the
    // ghosts themselves are positioned with sticky, so only their CONTENTS
    // depend on this state and a frame of lag is invisible.
    const [scrollLeftPx, setScrollLeftPx] = useState(0);
    const scrollRafRef = useRef<number | null>(null);
    const syncViewport = useCallback((el: HTMLDivElement) => {
        if (scrollRafRef.current != null) return;
        scrollRafRef.current = requestAnimationFrame(() => {
            scrollRafRef.current = null;
            setScrollLeftPx(el.scrollLeft);
        });
    }, []);
    useEffect(
        () => () => {
            if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
        },
        [],
    );
    const viewportLeftPx = scrollLeftPx;
    const viewportRightPx = containerWidth > 0 ? scrollLeftPx + containerWidth - TECH_LABEL_WIDTH : 0;

    // Jump to a specific track pixel. Track-local x maps 1:1 onto scrollLeft
    // (the sticky rail overlays the first TECH_LABEL_WIDTH of the viewport), so
    // landing a stop just right of the rail means scrolling to its leftPx.
    const scrollToTrackPx = useCallback(
        (trackPx: number) => {
            const el = swimlaneRef.current;
            if (!el) return;
            el.scrollTo({ left: Math.max(0, trackPx - 40), behavior: "smooth" });
        },
        [swimlaneRef],
    );

    // Smoothly scroll the horizontal timeline to its start/end so off-window
    // jobs (surfaced as edge chips on each tech row) can be reached.
    const scrollToEdge = useCallback(
        (side: "before" | "after") => {
            const el = swimlaneRef.current;
            if (!el) return;
            el.scrollTo({ left: side === "before" ? 0 : el.scrollWidth, behavior: "smooth" });
        },
        [swimlaneRef],
    );

    // ── #762: open the day where the work is, not at the window's left edge ──
    //
    // Priority on mount / date change:
    //   1. A saved position for this date (G5 — a Back-navigation return lands
    //      where the dispatcher left off, sessionStorage so it's per tab).
    //   2. Today, with "now" inside the window → put the now-line ~30% in from
    //      the left of the visible timeline, so what's happening is on screen.
    //   3. Any other date (or now outside the window) → the configured day
    //      window's start hour. In the default 7 AM–6 PM window that's the
    //      left edge already; in the full-day (24h) view it means the board
    //      opens at 7 AM, not a blank midnight — restoring the zoom preference
    //      no longer means opening on an empty board.
    //
    // Re-applies when the timeline scale settles (pxPerMin arrives async from
    // the ResizeObserver) but backs off permanently once the user scrolls.
    const userScrolledRef = useRef(false);
    const programmaticScrollRef = useRef(false);
    const scrollSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const initialScrollKey = `${selectedDate}|${fullDayView ? "full" : "window"}`;
    useEffect(() => {
        userScrolledRef.current = false;
    }, [initialScrollKey]);
    useEffect(() => {
        const el = swimlaneRef.current;
        if (!el || userScrolledRef.current) return;
        const saved = loadDayScroll(selectedDate);
        let left: number;
        if (saved != null) {
            left = saved;
        } else {
            const nowMin = (Date.now() - dayStartMs) / 60_000;
            if (selectedDate === localDateKey(new Date()) && nowMin >= 0 && nowMin <= dayMinutes) {
                const visible = Math.max(0, el.clientWidth - TECH_LABEL_WIDTH);
                left = Math.max(0, nowMin * pxPerMin - visible * 0.3);
            } else {
                left = Math.max(0, (configuredWindow.startHour - startHour) * 60 * pxPerMin);
            }
        }
        if (Math.abs(el.scrollLeft - left) > 1) {
            programmaticScrollRef.current = true;
            el.scrollLeft = left;
        }
        // Seed the viewport state even when the position did not move — on
        // first paint the board may already sit where it belongs, and no
        // scroll event would ever fire to tell the lanes what is on screen.
        setScrollLeftPx(el.scrollLeft);
    }, [
        initialScrollKey,
        pxPerMin,
        swimlaneRef,
        selectedDate,
        dayStartMs,
        dayMinutes,
        configuredWindow.startHour,
        startHour,
    ]);
    useEffect(
        () => () => {
            if (scrollSaveTimer.current) clearTimeout(scrollSaveTimer.current);
        },
        [],
    );
    const handleTimelineScroll = useCallback(() => {
        const el = swimlaneRef.current;
        if (!el) return;
        syncViewport(el);
        if (programmaticScrollRef.current) {
            programmaticScrollRef.current = false;
        } else {
            userScrolledRef.current = true;
        }
        // G5: remember the position (throttled) so a Back return restores it.
        if (scrollSaveTimer.current) clearTimeout(scrollSaveTimer.current);
        scrollSaveTimer.current = setTimeout(() => saveDayScroll(selectedDate, el.scrollLeft), 150);
    }, [swimlaneRef, selectedDate, syncViewport]);

    // Dominant "now" line — only on today, only while the current time falls
    // inside the visible day window. Refreshes once a minute so it drifts with
    // the clock without re-rendering the whole grid on every tick.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);
    const nowDate = new Date(nowMs);
    const todayKey = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, "0")}-${String(
        nowDate.getDate(),
    ).padStart(2, "0")}`;
    const nowMinutes = (nowMs - dayStartMs) / 60_000;
    const showNow = selectedDate === todayKey && nowMinutes >= 0 && nowMinutes <= dayMinutes;
    const nowOffsetPx = nowMinutes * pxPerMin;
    const nowLeft = TECH_LABEL_WIDTH + nowOffsetPx;
    const nowLabel = nowDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

    // Tracks the job currently being dragged. Falls back to local state when
    // the parent shell doesn't drive it explicitly (e.g. when only swimlane
    // -> swimlane drags happen).
    const [localDraggingJobId, setLocalDraggingJobId] = useState<string | null>(null);
    const draggingJobId = externalDraggingJobId ?? localDraggingJobId;
    const draggingJobTypeId = draggingJobId ? (jobJobTypeId?.get(draggingJobId) ?? null) : null;
    const draggingJobTypeLabel = draggingJobTypeId ? (jobTypeLabelById?.get(draggingJobTypeId) ?? null) : null;
    const draggingCity = draggingJobId ? (jobCityRestriction?.get(draggingJobId) ?? null) : null;
    const handleDragStart = (jobId: string) => {
        setLocalDraggingJobId(jobId);
        onJobDragStart?.(jobId);
    };
    const handleDragEnd = () => {
        setLocalDraggingJobId(null);
        onJobDragEnd?.();
    };

    const quickActions: JobQuickActions = {
        techs,
        onSchedule: onJobSchedule,
        onMoveToTomorrow: onJobMoveToTomorrow,
        onPushNextWeek: onJobPushNextWeek,
        onReassign: onJobReassign,
        onAddCrew: onJobAddCrew,
        onRemoveCrew: onJobRemoveCrew,
        onAdjustTime: onJobAdjustTime,
        onMoveToSpecificDate: onJobMoveToSpecificDate,
        onUnschedule: onJobUnschedule,
        onMessageTech: onJobMessageTech,
        onSkipJob: onJobSkip,
        onCancelJob: onJobCancel,
        onToggleConfirm: onJobToggleConfirm,
        moveToTomorrowLabel,
        pushNextWeekLabel,
    };

    // Work Inbox V2: one batched task-flag fetch for every card on the board;
    // JobBlock reads its own flag from context and renders the ⚑ corner tab.
    const flagJobIds = useMemo(() => {
        const ids: string[] = [];
        for (const jobs of techJobMap.values()) for (const j of jobs) ids.push(j.id);
        for (const j of unassignedJobs ?? []) ids.push(j.id);
        return ids;
    }, [techJobMap, unassignedJobs]);

    return (
        <JobTaskFlagProvider jobIds={flagJobIds}>
            <div
                ref={swimlaneRef}
                className="overflow-x-auto rounded-lg border bg-card"
                onScroll={handleTimelineScroll}
                onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                        handleDragEnd();
                    }
                }}
            >
                <div style={{ minWidth: TECH_LABEL_WIDTH + contentWidth + 16 }}>
                    {/* Hour ruler */}
                    {/* eslint-disable-next-line no-restricted-syntax -- D1: sticky header — content scrolls beneath it; the blur is load-bearing */}
                    <div className="sticky top-0 z-30 flex border-b bg-card/95 backdrop-blur">
                        {/* Frozen tech column header (#587): sticky on BOTH axes so
                        the "Technician" corner never scrolls away. Opaque bg so
                        cards can't show through when the board scrolls under it. */}
                        <div
                            className="sticky left-0 z-40 shrink-0 border-r bg-card px-2 py-2 text-xs font-semibold text-foreground/80"
                            style={{ width: TECH_LABEL_WIDTH }}
                        >
                            Technician
                        </div>
                        <div className="relative" style={{ width: contentWidth }}>
                            <div className="flex h-9 items-end">
                                {hours.slice(0, -1).map((h) => (
                                    <div
                                        key={h}
                                        className="border-r text-[11px] text-foreground/80"
                                        style={{ width: 60 * pxPerMin }}
                                    >
                                        <div className="-translate-y-1 px-1">
                                            {h % 12 === 0 ? 12 : h % 12}
                                            {h < 12 ? " am" : " pm"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* "Now" marker on the ruler — a labelled pill that
                            anchors the full-height line drawn over the rows. */}
                            {showNow && (
                                <div
                                    className="pointer-events-none absolute bottom-0 z-30 -translate-x-1/2"
                                    style={{ left: nowOffsetPx }}
                                >
                                    <span className="rounded-sm bg-red-600 px-1 py-0.5 text-[9px] font-bold leading-none text-white shadow-sm">
                                        {nowLabel}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Unassigned row — jobs scheduled for today with no tech */}
                    {unassignedJobs && unassignedJobs.length > 0 && (
                        <UnassignedRow
                            jobs={unassignedJobs}
                            dayStartMs={dayStartMs}
                            pxPerMin={pxPerMin}
                            contentWidth={contentWidth}
                            windowHours={windowHours}
                            density={boardDensity}
                            onJobClick={onJobClick}
                            onJobEdit={onJobEdit}
                            onJobDragStart={handleDragStart}
                            onJobDragEnd={handleDragEnd}
                            pulseByJob={pulseByJob}
                            quickActions={quickActions}
                            jobSkillReqs={jobSkillReqs}
                        />
                    )}

                    {/* Tech rows */}
                    <div className="relative">
                        {/* Dominant current-time line spanning every tech row.
                        pointer-events-none so it never blocks a drop. */}
                        {showNow && (
                            <div
                                className="pointer-events-none absolute top-0 bottom-0 z-30 w-0.5 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.7)]"
                                style={{ left: nowLeft }}
                            >
                                <span className="absolute -top-0.5 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-red-500" />
                            </div>
                        )}
                        {lanes.map(({ tech, blocks, drives, booked, bookedPoints, beforeCount, afterCount }) => {
                            const cap = tech.daily_capacity_minutes ?? DEFAULT_DAILY_CAPACITY_MINUTES;
                            const techApproved = techJobTypeApprovals?.get(tech.id) ?? null;
                            const techCities = techCityCerts?.get(tech.id) ?? null;
                            // Eligibility is evaluated whenever a drag is in
                            // progress AND the dragged job exposes either a
                            // job-type approval requirement or a city restriction.
                            // A job with neither (no job_type, unrestricted city)
                            // produces no greying — the user can drop freely.
                            const hasAnyCheck = draggingJobTypeId != null || draggingCity != null;
                            const eligibility = hasAnyCheck
                                ? computeEligibility(
                                      techApproved,
                                      draggingJobTypeId,
                                      draggingJobTypeLabel,
                                      draggingCity,
                                      techCities,
                                  )
                                : null;
                            const pairedHelpers = helpersByLead?.get(tech.id) ?? [];
                            return (
                                <TechRow
                                    key={tech.id}
                                    tech={tech}
                                    blocks={blocks}
                                    drives={drives}
                                    bookedMinutes={booked}
                                    bookedPoints={bookedPoints}
                                    pointsTarget={tech.daily_points_target ?? null}
                                    pointsCap={tech.daily_points_cap ?? null}
                                    capacityMinutes={cap}
                                    dayStartMs={dayStartMs}
                                    pxPerMin={pxPerMin}
                                    timelineWidth={contentWidth}
                                    windowHours={windowHours}
                                    density={boardDensity}
                                    onJobClick={onJobClick}
                                    onJobEdit={onJobEdit}
                                    onDrop={(jobId, techId, start, ineligibleReason) => {
                                        onDrop(jobId, techId, start, ineligibleReason);
                                        handleDragEnd();
                                    }}
                                    onJobDragStart={handleDragStart}
                                    onJobDragEnd={handleDragEnd}
                                    pulseByJob={pulseByJob}
                                    pairedHelpers={pairedHelpers}
                                    draggingHelperId={draggingHelperId}
                                    onHelperDragStart={onHelperDragStart}
                                    onHelperDragEnd={onHelperDragEnd}
                                    onHelperDrop={onHelperDrop}
                                    onHelperRevert={onHelperRevert}
                                    onTechDrop={onTechDrop}
                                    eligibility={eligibility}
                                    quickActions={quickActions}
                                    skillCodes={techSkills?.get(tech.id)}
                                    jobSkillReqs={jobSkillReqs}
                                    beforeCount={beforeCount}
                                    afterCount={afterCount}
                                    onScrollToEdge={scrollToEdge}
                                    viewportLeftPx={viewportLeftPx}
                                    viewportRightPx={viewportRightPx}
                                    onScrollToTrackPx={scrollToTrackPx}
                                    isToday={isToday}
                                    unslotted={unslottedByTech?.get(tech.id)}
                                    out={outByTech?.get(tech.id) ?? null}
                                    onMarkOut={onMarkOut}
                                    onOutClick={onOutClick}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </JobTaskFlagProvider>
    );
}

/* ─── Unassigned row ────────────────────────────────────────────────── */

interface UnassignedRowProps {
    jobs: DispatchJob[];
    dayStartMs: number;
    pxPerMin: number;
    /** Full lane track width (widest packed lane) so the unassigned track
     *  lines up with the tech lanes. */
    contentWidth: number;
    windowHours: number;
    density?: BoardCardDensity;
    onJobClick: (jobId: string) => void;
    onJobEdit?: (jobId: string) => void;
    onJobDragStart?: (jobId: string) => void;
    onJobDragEnd?: () => void;
    pulseByJob?: Map<string, JobPulseEntry>;
    quickActions?: JobQuickActions;
    /** jobId -> required skill codes, for the per-block skill chips. */
    jobSkillReqs?: Map<string, string[]>;
}

function UnassignedRow({
    jobs,
    dayStartMs,
    pxPerMin,
    contentWidth,
    windowHours,
    density = "comfortable",
    onJobClick,
    onJobEdit,
    onJobDragStart,
    onJobDragEnd,
    pulseByJob,
    quickActions,
    jobSkillReqs,
}: UnassignedRowProps) {
    const dayStart = useMemo(() => new Date(dayStartMs), [dayStartMs]);
    const blocks = useMemo(
        () => buildPositionedBlocks(jobs, dayStart, pxPerMin, windowHours, density).blocks,
        [jobs, dayStart, pxPerMin, windowHours, density],
    );
    const gridlineCount = Math.max(0, windowHours - 1);

    return (
        <div className="flex border-b border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20">
            <div
                className="sticky left-0 z-30 shrink-0 border-r bg-amber-50 px-2 py-1.5 dark:bg-amber-950/60"
                style={{ width: TECH_LABEL_WIDTH }}
            >
                <div className="flex items-center justify-between gap-1">
                    <span className="truncate text-xs font-semibold text-amber-800 dark:text-amber-200">
                        Unassigned
                    </span>
                    <Badge
                        variant="secondary"
                        className="text-[10px] border-amber-300 text-amber-800 dark:text-amber-200"
                    >
                        {blocks.length}
                    </Badge>
                </div>
                <p className="mt-1 text-[10px] leading-tight text-amber-700/80 dark:text-amber-300/70">
                    Drag onto a tech to assign
                </p>
            </div>
            <div className="relative h-20" style={{ width: contentWidth }}>
                {Array.from({ length: gridlineCount }, (_, i) => (
                    <div
                        key={i}
                        className="absolute inset-y-0 border-r border-dashed border-amber-200/60 dark:border-amber-800/40"
                        style={{ left: (i + 1) * 60 * pxPerMin }}
                    />
                ))}
                {blocks.map((b) => (
                    <JobBlock
                        key={b.job.id}
                        block={b}
                        techId=""
                        density={density}
                        onJobClick={onJobClick}
                        onJobEdit={onJobEdit}
                        onDragStart={onJobDragStart}
                        onDragEnd={onJobDragEnd}
                        pulse={pulseByJob?.get(b.job.id) ?? null}
                        quickActions={quickActions}
                        requiredSkillCodes={jobSkillReqs?.get(b.job.id)}
                    />
                ))}
            </div>
        </div>
    );
}

/* ─── Tech row ──────────────────────────────────────────────────────── */

interface TechRowProps {
    tech: DispatchTech;
    blocks: PositionedBlock[];
    drives: DriveSegment[];
    bookedMinutes: number;
    bookedPoints?: number;
    pointsTarget?: number | null;
    pointsCap?: number | null;
    capacityMinutes: number;
    dayStartMs: number;
    pxPerMin: number;
    /** Full width of the lane track in px (the widest packed lane on the
     *  board, so every lane's clock ruler + drop math line up). */
    timelineWidth: number;
    windowHours: number;
    /** Board card density — sizes cards, lane height, and card chrome. */
    density?: BoardCardDensity;
    onJobClick: (jobId: string) => void;
    onJobEdit?: (jobId: string) => void;
    /**
     * Drop handler. `ineligibleReason` is non-null when the dispatcher is
     * forcing the assignment despite the tech missing the required skill or
     * city certification. The shell should still apply the assignment but
     * surface a warning toast so the override is intentional.
     */
    onDrop: (jobId: string, newTechId: string, newStart: Date, ineligibleReason?: string | null) => void;
    onJobDragStart?: (jobId: string) => void;
    onJobDragEnd?: () => void;
    pulseByJob?: Map<string, JobPulseEntry>;
    /** Helper sticker chips riding with this lead today — pinned to the
     *  lane header, never their own row. */
    pairedHelpers?: HelperChipInfo[];
    draggingHelperId?: string | null;
    onHelperDragStart?: (helperId: string) => void;
    onHelperDragEnd?: () => void;
    onHelperDrop?: (helperId: string, targetLeadId: string, x: number, y: number) => void;
    onHelperRevert?: (helper: HelperChipInfo) => void;
    onTechDrop?: (techId: string, targetLeadId: string, x: number, y: number) => void;
    eligibility?: { eligible: boolean; missingApprovalLabel: string | null; missingCityLabel: string | null } | null;
    quickActions?: JobQuickActions;
    /** Active skill codes for this tech, rendered as chips under the name. */
    skillCodes?: string[];
    /** jobId -> required skill codes, for the per-block skill chips. */
    jobSkillReqs?: Map<string, string[]>;
    /** Jobs scheduled entirely before / after the visible window, surfaced as
     *  clickable edge chips that scroll the timeline to them. */
    beforeCount?: number;
    afterCount?: number;
    onScrollToEdge?: (side: "before" | "after") => void;
    /** #975 — the live viewport in track-local px. Drives the ghost cards and
     *  the rail's "N off screen" badge. */
    viewportLeftPx?: number;
    viewportRightPx?: number;
    onScrollToTrackPx?: (trackPx: number) => void;
    /** #826: today gets the live Behind/On-track badge; other days get none. */
    isToday?: boolean;
    /** #826 (audit C4): this tech's assigned-but-untimed jobs. */
    unslotted?: { count: number; labels: string[] };
    /** #1268 — set when this tech is blocked out on the shown date. */
    out?: OutState | null;
    onMarkOut?: (techId: string) => void;
    onOutClick?: (userId: string) => void;
}

/**
 * #975 — a compressed peek at the stops scrolled out of view, pinned to the
 * VIEWPORT edge of a lane rather than the canvas edge.
 *
 * Placement is CSS sticky, not a computed offset, so the ghost never lags a
 * scroll frame; only its contents come from React state. It is deliberately
 * dashed and tinted so it can never be mistaken for a real stop, and it is
 * never a drop target and never draggable — the board's packing and
 * drop-landing math must stay the single truth about where work goes.
 */
function OffscreenGhost({
    side,
    dir,
    compact,
    onJump,
}: {
    side: OffscreenSide;
    dir: "before" | "after";
    compact: boolean;
    onJump: () => void;
}) {
    const tradeHex = TRADE_HEX[side.nearest.job.trade_code ?? ""] ?? "#64748b";
    const time = new Date(side.nearestStartMs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const plural = side.count === 1 ? "" : "s";
    // The tab drops the time and points from view, so the label has to carry
    // them — a touch user has no hover to fall back on.
    const label =
        `${side.count} stop${plural} off screen ${dir === "before" ? "earlier" : "later"} — ` +
        `${dir === "before" ? "nearest" : "first"} at ${time}, ${side.points} pts. Click to jump there.`;

    return (
        <button
            type="button"
            onClick={onJump}
            title={label}
            aria-label={label}
            data-testid={`offscreen-ghost-${dir}`}
            className={cn(
                "absolute top-0 -translate-y-1/2 rounded-lg border border-dashed border-amber-500 bg-amber-50/95 px-1.5 py-1 text-left shadow-md backdrop-blur-sm transition-colors hover:bg-amber-100 dark:bg-amber-950/90 dark:hover:bg-amber-900/90",
                dir === "before" ? "left-2" : "right-2",
            )}
            style={{
                width: compact ? GHOST_TAB_WIDTH : GHOST_WIDTH,
                borderLeftWidth: 4,
                borderLeftStyle: "solid",
                borderLeftColor: tradeHex,
            }}
        >
            {compact ? (
                <>
                    <span className="block text-center text-[12px] font-extrabold leading-none text-amber-900 dark:text-amber-200">
                        {dir === "before" ? "←" : "→"}
                    </span>
                    <span className="block text-center text-[12.5px] font-extrabold leading-tight tabular-nums text-amber-800 dark:text-amber-300">
                        {side.count}
                    </span>
                </>
            ) : (
                <>
                    <span className="block text-[11.5px] font-extrabold leading-tight text-amber-900 dark:text-amber-200">
                        {dir === "before" ? `← ${side.count} earlier` : `${side.count} later →`}
                    </span>
                    <span className="mt-0.5 block text-[10px] font-bold tabular-nums text-amber-800 dark:text-amber-300">
                        {time}
                    </span>
                    <span className="block text-[9.5px] text-amber-700 dark:text-amber-400">
                        {side.points} pts hidden
                    </span>
                </>
            )}
        </button>
    );
}

function TechRow({
    tech,
    blocks,
    drives,
    bookedMinutes,
    bookedPoints,
    pointsTarget,
    pointsCap,
    capacityMinutes,
    dayStartMs,
    pxPerMin,
    timelineWidth,
    windowHours,
    density = "comfortable",
    onJobClick,
    onJobEdit,
    onDrop,
    onJobDragStart,
    onJobDragEnd,
    pulseByJob,
    pairedHelpers,
    draggingHelperId,
    onHelperDragStart,
    onHelperDragEnd,
    onHelperDrop,
    onHelperRevert,
    onTechDrop,
    eligibility,
    quickActions,
    skillCodes,
    jobSkillReqs,
    beforeCount = 0,
    afterCount = 0,
    onScrollToEdge,
    viewportLeftPx = 0,
    viewportRightPx = 0,
    onScrollToTrackPx,
    isToday = true,
    unslotted,
    out = null,
    onMarkOut,
    onOutClick,
}: TechRowProps) {
    const trackRef = useRef<HTMLDivElement | null>(null);
    const [hover, setHover] = useState<DropLanding | null>(null);
    // Helper-chip drag hovering over this lane header (drop target).
    const [chipHover, setChipHover] = useState(false);

    // #628 item 2: cursor → actual landing (packed-lane aware), shared by the
    // hover preview and the drop itself so what the guide promises is what
    // the drop does.
    const landingAtClientX = useCallback(
        (clientX: number): DropLanding => {
            if (!trackRef.current) return { minutes: 0, guidePx: 0, afterJobId: null };
            const rect = trackRef.current.getBoundingClientRect();
            const px = clamp(clientX - rect.left, 0, timelineWidth);
            return landingForDrop(px, blocks, pxPerMin, dayStartMs);
        },
        [pxPerMin, timelineWidth, blocks, dayStartMs],
    );

    const ineligible = eligibility != null && !eligibility.eligible;
    const dragActive = eligibility != null;

    // Per-row state surfaced in the header so a dispatcher can read every
    // tech's load without opening anything. "Remaining" is the tech's own
    // work — the jobs on his iPad (#1609). Office review, ready for invoice,
    // held and awaiting-scheduling jobs are the office's and ride as a
    // separate "+N office" count instead of inflating his number; "behind"
    // trips when the day is over capacity or any job is flagged at-risk /
    // overdue by the live pulse.
    const isEmpty = blocks.length === 0;
    const laneCounts = techCounts(blocks.map((b) => b.job));
    const jobsRemaining = laneCounts.left;
    const leftChip = leftChipText(laneCounts);
    // The office's share rides on the status line, not beside the name, so the
    // name keeps its room (#1609).
    const officeChip = leftChip.office ? (
        <span
            className="shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-500/20 dark:text-violet-200"
            title={leftChip.title}
            data-testid="lane-office-chip"
        >
            {leftChip.office}
        </span>
    ) : null;

    // #975 — what is scrolled out of view right now, and the rail's honest
    // total. `beforeCount` / `afterCount` are a DIFFERENT population: jobs
    // outside the clock window entirely, which are never in `blocks` and can
    // never be reached by scrolling. They are folded into the badge's count so
    // the rail states one complete truth, and called out in its tooltip.
    const ghosts = computeOffscreenGhosts(blocks, viewportLeftPx, viewportRightPx);
    const hiddenInViewport = (ghosts.before?.count ?? 0) + (ghosts.after?.count ?? 0);
    const outsideWindow = beforeCount + afterCount;
    const offscreenTotal = hiddenInViewport + outsideWindow;
    const allOffscreen = offscreenTotal > 0 && offscreenTotal >= blocks.length + outsideWindow;
    const offscreenArrow =
        (ghosts.before || beforeCount > 0) && (ghosts.after || afterCount > 0)
            ? "↔"
            : ghosts.before || beforeCount > 0
              ? "←"
              : "→";
    const ghostCompact = viewportRightPx - viewportLeftPx < GHOST_TAB_MAX_PX;
    // B4: a lane never shows both a lead-in rail and a ghost. The lead-in only
    // means anything while the first stop is genuinely on screen; otherwise the
    // ghost owns the row's message and a lead-in would contradict it.
    const firstBlock = blocks[0];
    // The lead-in spans the VISIBLE part of the gap, not the whole gap from
    // track zero. Drawn from zero, its centered label lands wherever the middle
    // of the gap happens to be — which on a scrolled board is usually off
    // screen, so the one cue meant to explain an empty morning was itself
    // invisible. Clamping the left edge to the viewport keeps the label in
    // front of the dispatcher.
    const leadInLeft = Math.max(0, viewportLeftPx);
    const leadInWidth = firstBlock ? firstBlock.leftPx - leadInLeft : 0;
    const showLeadIn =
        firstBlock != null &&
        viewportRightPx > viewportLeftPx &&
        leadInWidth > LEAD_IN_MIN_PX &&
        firstBlock.leftPx < viewportRightPx;
    const jumpTo = (side: OffscreenSide) => () => onScrollToTrackPx?.(side.nearest.leftPx);
    const clockLabel = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const behind =
        bookedMinutes > capacityMinutes ||
        blocks.some((b) => {
            const r = pulseByJob?.get(b.job.id)?.risk;
            return r === "at_risk" || r === "overdue";
        });

    const tooltip = (() => {
        if (!ineligible || !eligibility) return undefined;
        const parts: string[] = [];
        if (eligibility.missingApprovalLabel) {
            parts.push(`Not approved for ${eligibility.missingApprovalLabel}`);
        }
        if (eligibility.missingCityLabel) {
            parts.push(`Not approved for ${eligibility.missingCityLabel}`);
        }
        return parts.join(" · ");
    })();

    // While a job is dragged over this row, hover is set — light up the
    // whole row so the dispatcher sees exactly which tech they're about to
    // drop on, and surface the ACTUAL landing time as a floating tag (in a
    // packed lane that is "right after the stop under the cursor", not the
    // raw pixel's clock time — #628 item 2).
    const hovering = hover != null;
    const hoverLabel = hover
        ? `${new Date(dayStartMs + hover.minutes * 60_000).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
          })}${hover.afterJobId ? " · right after this stop" : ""}`
        : null;

    return (
        <div
            className={cn(
                "flex border-b last:border-b-0 transition-opacity",
                ineligible && "opacity-40",
                hovering && "bg-primary/5 ring-1 ring-inset ring-primary/40",
            )}
            title={tooltip}
        >
            {/* #975 — zero-width sticky sentinels that pin the ghost cards to
                the VIEWPORT edges. `sentL` must be the row's first child and
                `sentR` its last: sticky-left only holds an element back as it
                would scroll off to the left, and sticky-right only holds one
                back on the right, so each has to start from the side it
                defends. Both are 0x0 and take no part in the flex layout. */}
            {ghosts.before && !isEmpty && (
                <div
                    className="sticky z-20 h-0 w-0 self-center"
                    style={{ left: TECH_LABEL_WIDTH }}
                    data-testid="offscreen-sentinel-before"
                >
                    <OffscreenGhost
                        side={ghosts.before}
                        dir="before"
                        compact={ghostCompact}
                        onJump={jumpTo(ghosts.before)}
                    />
                </div>
            )}

            {/* Tech label — frozen column (#587: sticky-left, opaque bg so it
                never scrolls off and cards can't bleed through) and also the
                drop target for helper sticker chips. */}
            <div
                className={cn(
                    "sticky left-0 z-30 shrink-0 border-r bg-card px-2 py-1.5",
                    dragActive && eligibility?.eligible && "ring-2 ring-emerald-300 ring-inset",
                    draggingHelperId != null && draggingHelperId !== tech.id && "ring-2 ring-inset ring-sky-400/70",
                    chipHover && "bg-sky-100/70 dark:bg-sky-900/40",
                )}
                style={{ width: TECH_LABEL_WIDTH }}
                onDragOver={(e) => {
                    if (
                        !e.dataTransfer.types.includes(HELPER_DRAG_MIME) &&
                        !e.dataTransfer.types.includes(TECH_DRAG_MIME)
                    )
                        return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setChipHover(true);
                }}
                onDragLeave={() => setChipHover(false)}
                onDrop={(e) => {
                    setChipHover(false);
                    const helperId = e.dataTransfer.getData(HELPER_DRAG_MIME);
                    if (helperId) {
                        if (helperId === tech.id) return;
                        e.preventDefault();
                        onHelperDrop?.(helperId, tech.id, e.clientX, e.clientY);
                        return;
                    }
                    // V3: a tech's name dropped here — demote him to
                    // acting-helper on this lead for the chosen scope.
                    const techId = e.dataTransfer.getData(TECH_DRAG_MIME);
                    if (!techId || techId === tech.id) return;
                    e.preventDefault();
                    onTechDrop?.(techId, tech.id, e.clientX, e.clientY);
                }}
            >
                <div className="flex items-center justify-between gap-1">
                    <span
                        className="cursor-grab truncate text-xs font-semibold active:cursor-grabbing"
                        draggable
                        title="Drag onto another tech's lane to pair as helper for the day"
                        onDragStart={(e) => {
                            e.dataTransfer.setData(TECH_DRAG_MIME, tech.id);
                            e.dataTransfer.effectAllowed = "move";
                        }}
                    >
                        {tech.first_name} {tech.last_name}
                    </span>
                    {onMarkOut && (
                        <button
                            type="button"
                            data-testid="mark-out-button"
                            className="shrink-0 rounded p-0.5 text-foreground/40 transition-colors hover:bg-secondary hover:text-foreground"
                            title={`Mark ${tech.first_name} out — a class, a school week, jury duty. Takes them off the auto-scheduler and says so here.`}
                            aria-label={`Mark ${tech.first_name} ${tech.last_name} out`}
                            onClick={() => onMarkOut(tech.id)}
                        >
                            <CalendarOff className="h-3 w-3" />
                        </button>
                    )}
                    {!isEmpty && (
                        <span
                            className={cn(
                                "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                                jobsRemaining > 0 ? "bg-primary/10 text-primary" : "bg-muted text-foreground/60",
                            )}
                            title={leftChip.title}
                            data-testid="lane-left-chip"
                        >
                            {/* #828 m2: "1 left" alone couldn't distinguish one
                                job total from one job remaining — say both.
                                #1609: only the tech's own work counts. */}
                            {leftChip.main}
                        </span>
                    )}
                </div>
                {/* #1268 — the day is spoken for. The lane is NOT hidden: a tech
                    who is out while still holding work is exactly the case the
                    dispatcher has to see, and a missing lane reads as "nobody
                    by that name" rather than "nobody available". */}
                {out && (
                    <div className="mt-1">
                        <button
                            type="button"
                            data-testid="out-badge"
                            disabled={!onOutClick}
                            onClick={() => onOutClick?.(tech.id)}
                            className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700 transition-colors enabled:cursor-pointer enabled:hover:bg-slate-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary dark:bg-slate-600 dark:text-slate-50 dark:enabled:hover:bg-slate-500"
                            title={
                                out.clearable
                                    ? `${tech.first_name} is marked out today — ${out.label}. The auto-scheduler skips them. Click to move or clear.`
                                    : `${tech.first_name} is off today — ${out.label}. This one comes from a PTO request or a call-in, so it's cleared by undoing that record.`
                            }
                        >
                            <CalendarOff className="h-3 w-3 shrink-0" />
                            <span className="truncate">{outBadgeText(out)}</span>
                        </button>
                    </div>
                )}
                {(skillCodes?.length ?? 0) > 0 && (
                    <div className="mt-1 flex flex-wrap gap-0.5">
                        {(skillCodes ?? []).map((code) => (
                            <span
                                key={code}
                                className="inline-flex items-center rounded bg-indigo-100 px-1 py-px text-[9px] font-medium tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                                title={`Skill: ${formatSkillCode(code)}`}
                            >
                                {formatSkillCode(code)}
                            </span>
                        ))}
                    </div>
                )}
                {isEmpty ? (
                    /* #826 (audit C4): "No jobs scheduled" was a lie when the
                       tech held assigned work with no time on it — a time grid
                       can't draw a timeless job, so the row actively said the
                       tech was free. The untimed chip below carries the truth
                       either way; this line only claims what it knows. */
                    <p className="mt-0.5 text-[10px] italic text-muted-foreground">
                        {(unslotted?.count ?? 0) > 0 ? "Nothing on the clock" : "No jobs scheduled"}
                    </p>
                ) : (
                    <>
                        {/* #826 (audit C3): the badge measures elapsed time
                            against plan, so on a future day it could only ever
                            be green — it renders only on today. */}
                        {(isToday || officeChip) && (
                            <div className="mt-1 flex items-center gap-1">
                                {isToday && (
                                    <span
                                        className={cn(
                                            "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                                            behind && jobsRemaining > 0
                                                ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                                                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                "h-1.5 w-1.5 rounded-full",
                                                jobsRemaining === 0
                                                    ? "bg-emerald-500"
                                                    : behind
                                                      ? "bg-red-500"
                                                      : "bg-emerald-500",
                                            )}
                                        />
                                        {/* #828 M13: a tech with nothing left is DONE, not
                                        "Behind" — the overbooked-day math kept the red
                                        badge on a finished lane while Command Center
                                        called the same tech "available". */}
                                        {jobsRemaining === 0 ? "Done for the day" : behind ? "Behind" : "On track"}
                                    </span>
                                )}
                                {officeChip}
                            </div>
                        )}
                        <div className="mt-1.5">
                            <CapacityStrip
                                bookedMinutes={bookedMinutes}
                                capacityMinutes={capacityMinutes}
                                bookedPoints={bookedPoints}
                                pointsTarget={pointsTarget}
                                pointsCap={pointsCap}
                            />
                        </div>
                    </>
                )}
                {(unslotted?.count ?? 0) > 0 && (
                    <div className="mt-1">
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-300"
                            title={`Assigned to ${tech.first_name} with no scheduled time — never drawn on this board. Set a time from the job page:\n${(
                                unslotted?.labels ?? []
                            ).join("\n")}`}
                            data-testid="unslotted-chip"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            {unslotted!.count} assigned, no time
                        </span>
                    </div>
                )}
                {/* #975 — "N off screen". Fires the moment ANY stop leaves the
                    viewport, not only when the whole day has: the rail is the
                    part that is always visible, so it is the part that must
                    never read as idle while the tech is booked. Clicking it
                    jumps to the nearest hidden stop. */}
                {offscreenTotal > 0 && (
                    <div className="mt-1">
                        <button
                            type="button"
                            onClick={() => {
                                const side = ghosts.after ?? ghosts.before;
                                if (side) onScrollToTrackPx?.(side.nearest.leftPx);
                                else onScrollToEdge?.(afterCount > 0 ? "after" : "before");
                            }}
                            className={cn(
                                "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold transition-colors",
                                allOffscreen
                                    ? "bg-amber-200 text-amber-900 ring-1 ring-amber-400 hover:bg-amber-300 dark:bg-amber-500/40 dark:text-amber-100 dark:ring-amber-500"
                                    : "bg-amber-100 text-amber-800 ring-1 ring-amber-300 hover:bg-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-700",
                            )}
                            title={[
                                `${offscreenTotal} of ${blocks.length + outsideWindow} stop${
                                    blocks.length + outsideWindow === 1 ? "" : "s"
                                } are out of view${allOffscreen ? " — this tech's whole day is off screen" : ""}.`,
                                outsideWindow > 0
                                    ? `${outsideWindow} of them ${outsideWindow === 1 ? "is" : "are"} scheduled outside the visible hours — switch to the 24-hour view to see ${outsideWindow === 1 ? "it" : "them"}.`
                                    : null,
                                hiddenInViewport > 0 ? "Click to jump to the nearest one." : null,
                            ]
                                .filter(Boolean)
                                .join(" ")}
                            data-testid="offscreen-chip"
                        >
                            {offscreenArrow} {offscreenTotal} off screen
                        </button>
                    </div>
                )}
                {/* Helper sticker chips pinned to the lane header (rendered
                    even on empty lanes — the pairing exists regardless). */}
                {(pairedHelpers?.length ?? 0) > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                        {(pairedHelpers ?? []).map((h) => (
                            <HelperChip
                                key={h.id}
                                helper={h}
                                onDragStart={(id) => onHelperDragStart?.(id)}
                                onDragEnd={() => onHelperDragEnd?.()}
                                onRevert={onHelperRevert}
                                onOutClick={onOutClick ? (helper) => onOutClick(helper.id) : undefined}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Time track. Drops are always permitted (so dispatchers can
                override skill / city eligibility), but we forward the
                ineligible reason so the shell can surface a warning toast
                instead of failing silently. */}
            <div
                ref={trackRef}
                className={cn(
                    "relative bg-background transition-[height]",
                    isEmpty ? "h-9" : density === "compact" ? "h-24" : "h-32",
                    ineligible && "cursor-help",
                )}
                style={{ width: timelineWidth }}
                onDragOver={(e) => {
                    // Helper sticker chips / tech-name drags only land on lane
                    // headers or the bench — never on the time track (job drop).
                    if (
                        e.dataTransfer.types.includes(HELPER_DRAG_MIME) ||
                        e.dataTransfer.types.includes(TECH_DRAG_MIME)
                    )
                        return;
                    e.preventDefault();
                    // Always "move" — the ineligible path is an override:
                    // the drop must be allowed at the browser level so our
                    // own handleDrop can fire the warning toast. Setting
                    // dropEffect = "copy" here used to trigger a browser-
                    // level rejection (the drag source's effectAllowed is
                    // "move"), which the user saw as the "no" cursor even
                    // though the dispatcher's intent was an override.
                    e.dataTransfer.dropEffect = "move";
                    setHover(landingAtClientX(e.clientX));
                }}
                onDragLeave={() => setHover(null)}
                onDrop={(e) => {
                    e.preventDefault();
                    const jobId = e.dataTransfer.getData("text/plain");
                    if (!jobId) {
                        setHover(null);
                        return;
                    }
                    const { minutes } = landingAtClientX(e.clientX);
                    const start = new Date(dayStartMs + minutes * 60_000);
                    onDrop(
                        jobId,
                        tech.id,
                        start,
                        ineligible ? (tooltip ?? "Tech is missing required eligibility") : null,
                    );
                    setHover(null);
                }}
            >
                {/* Idle tech — slim "no jobs" state that is still a drop
                    target so a dispatcher can drop the first job onto it. */}
                {isEmpty && (
                    <div className="pointer-events-none flex h-full items-center px-2 text-[11px] italic text-foreground/30">
                        No jobs scheduled — drop here to assign
                    </div>
                )}

                {/* Vertical hour gridlines */}
                {!isEmpty &&
                    Array.from({ length: Math.max(0, windowHours - 1) }, (_, i) => (
                        <div
                            key={i}
                            className="absolute inset-y-0 border-r border-dashed border-border/60"
                            style={{ left: (i + 1) * 60 * pxPerMin }}
                        />
                    ))}

                {/* PRE-CUTOVER #41 — drive-time visualization between
                    consecutive jobs on the tech's day. Uses a haversine
                    estimate from the two stops' coordinates when
                    available, falling back to the raw schedule gap so
                    older / un-geocoded sites still get a visual bar.
                    This is purely visual; the auto-scheduler and the
                    capacity strip ignore it. */}
                {drives.map((d, i) => {
                    const minutes = d.driveMinutes ?? d.gapMinutes;
                    const isEstimatedDrive = d.driveMinutes != null;
                    const tooltip = isEstimatedDrive
                        ? `≈ ${d.driveMinutes} min drive (gap on schedule: ${d.gapMinutes} min)`
                        : `${d.gapMinutes} min gap on schedule`;
                    return (
                        <div
                            key={i}
                            className={cn(
                                "absolute top-1/2 h-1 -translate-y-1/2 rounded-full",
                                isEstimatedDrive
                                    ? "bg-slate-400/60 dark:bg-slate-500/40"
                                    : "bg-amber-300/70 dark:bg-amber-400/30",
                            )}
                            style={{ left: d.leftPx, width: d.widthPx }}
                            title={tooltip}
                        >
                            {d.widthPx > 56 && (
                                <span
                                    className={cn(
                                        "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded px-1 py-px text-[9px] font-medium shadow-sm",
                                        isEstimatedDrive ? "bg-slate-600/90 text-white" : "bg-amber-700/90 text-white",
                                    )}
                                >
                                    {isEstimatedDrive ? `≈ ${minutes}m drive` : `${minutes}m gap`}
                                </span>
                            )}
                        </div>
                    );
                })}

                {/* Hover indicator — vertical landing guide + floating time tag.
                    The guide sits where the job will actually land (#628): in a
                    packed lane that's the seam right after the stop under the
                    cursor, so the preview and the drop can never disagree. */}
                {hover != null && (
                    <div
                        className="pointer-events-none absolute inset-y-2 z-10 w-0.5 bg-primary"
                        style={{ left: hover.guidePx }}
                    >
                        {hoverLabel && (
                            <span className="absolute -top-1 left-1 whitespace-nowrap rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-primary-foreground shadow-md">
                                {hoverLabel}
                            </span>
                        )}
                    </div>
                )}

                {/* #975 — late-start lead-in. The dead space before a tech's
                    first stop used to read as "nothing here"; this says the day
                    simply starts later. Only drawn while that first stop is
                    actually on screen (B4) — otherwise the ghost is the row's
                    message and a lead-in would contradict it. */}
                {showLeadIn && firstBlock && (
                    <div
                        className="pointer-events-none absolute top-1/2 flex items-center justify-center border-t border-dashed border-border"
                        style={{ left: leadInLeft, width: leadInWidth }}
                        aria-hidden="true"
                    >
                        <span className="-translate-y-1/2 whitespace-nowrap bg-background px-2 text-[10px] font-medium text-muted-foreground">
                            first stop {clockLabel(firstBlock.startMs)}
                        </span>
                    </div>
                )}

                {/* Job blocks */}
                {blocks.map((b) => (
                    <JobBlock
                        key={b.job.id}
                        block={b}
                        techId={tech.id}
                        density={density}
                        onJobClick={onJobClick}
                        onJobEdit={onJobEdit}
                        onDragStart={onJobDragStart}
                        onDragEnd={onJobDragEnd}
                        pulse={pulseByJob?.get(b.job.id) ?? null}
                        quickActions={quickActions}
                        requiredSkillCodes={jobSkillReqs?.get(b.job.id)}
                    />
                ))}

                {/* #975 — the old "← N earlier" / "N later →" chips lived
                    here, absolutely positioned at left-1 / right-1 inside a
                    track that is the FULL board width (~2,880px in the 24-hour
                    view). They were themselves off screen in every case they
                    fired: you had to scroll to the end of the board to see the
                    chip telling you to scroll to the end of the board. Their
                    count is now folded into the rail badge above, and the edge
                    affordance is the sticky ghost rendered outside this track. */}
            </div>

            {ghosts.after && !isEmpty && (
                <div className="sticky right-0 z-20 h-0 w-0 self-center" data-testid="offscreen-sentinel-after">
                    <OffscreenGhost
                        side={ghosts.after}
                        dir="after"
                        compact={ghostCompact}
                        onJump={jumpTo(ghosts.after)}
                    />
                </div>
            )}
        </div>
    );
}

interface JobBlockProps {
    block: PositionedBlock;
    techId: string;
    /** Board card density — hides the address line + tightens padding in
     *  compact so more of the day fits on wide screens. */
    density?: BoardCardDensity;
    onJobClick: (jobId: string) => void;
    /** Pre-cutover #37 — pencil affordance opens /jobs/:id/edit. */
    onJobEdit?: (jobId: string) => void;
    onDragStart?: (jobId: string) => void;
    onDragEnd?: () => void;
    pulse: JobPulseEntry | null;
    quickActions?: JobQuickActions;
    /** Skill codes required by this job's job type. Rendered as chips so a
     *  dispatcher can see at a glance what credentials the job needs. */
    requiredSkillCodes?: string[];
}

interface JobActionItemsProps {
    job: DispatchJob;
    lead: string;
    onJobClick: (jobId: string) => void;
    onJobEdit?: (jobId: string) => void;
    hasSchedule: boolean;
    hasAdjustTime: boolean;
    hasMoveToSpecificDate: boolean;
    hasUnschedule: boolean;
    hasMessageTech: boolean;
    hasMoveActions: boolean;
    hasReassign: boolean;
    hasAddCrew: boolean;
    hasSkip: boolean;
    hasCancel: boolean;
    reassignTechs: DispatchTech[];
    addCrewTechs: DispatchTech[];
    quickActions?: JobQuickActions;
    MenuItem: typeof DropdownMenuItem;
    MenuLabel: typeof DropdownMenuLabel;
    MenuSeparator: typeof DropdownMenuSeparator;
}

/** Shared quick-action list rendered inside both the right-click ContextMenu
 *  and the hover kebab DropdownMenu. */
function JobActionItems({
    job,
    lead,
    onJobClick,
    onJobEdit,
    hasSchedule,
    hasAdjustTime,
    hasMoveToSpecificDate,
    hasUnschedule,
    hasMessageTech,
    hasMoveActions,
    hasReassign,
    hasAddCrew,
    hasSkip,
    hasCancel,
    reassignTechs,
    addCrewTechs,
    quickActions,
    MenuItem,
    MenuLabel,
    MenuSeparator,
}: JobActionItemsProps) {
    return (
        <>
            <MenuLabel className="truncate">{lead}</MenuLabel>
            <MenuSeparator />
            <MenuItem onSelect={() => onJobClick(job.id)}>Open job</MenuItem>
            {onJobEdit && <MenuItem onSelect={() => onJobEdit(job.id)}>Edit job</MenuItem>}
            {quickActions?.onToggleConfirm && (
                <MenuItem onSelect={() => quickActions.onToggleConfirm?.(job.id, confirmMenuAction(job).nextConfirm)}>
                    {confirmMenuAction(job).label}
                </MenuItem>
            )}
            {hasSchedule && (
                <>
                    <MenuSeparator />
                    <MenuItem onSelect={() => quickActions?.onSchedule?.(job.id)}>Schedule…</MenuItem>
                </>
            )}
            {hasReassign && (
                <>
                    <MenuSeparator />
                    <MenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Reassign to
                    </MenuLabel>
                    <div className="max-h-44 overflow-y-auto">
                        {reassignTechs.map((t) => (
                            <MenuItem key={t.id} onSelect={() => quickActions?.onReassign?.(job.id, t.id)}>
                                {t.first_name} {t.last_name}
                            </MenuItem>
                        ))}
                    </div>
                </>
            )}
            {hasAddCrew && (
                <>
                    <MenuSeparator />
                    <MenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Add tech to this job
                    </MenuLabel>
                    <div className="max-h-44 overflow-y-auto">
                        {addCrewTechs.map((t) => (
                            <MenuItem
                                key={t.id}
                                onSelect={() => quickActions?.onAddCrew?.(job.id, t.id)}
                                title="Both techs work this job. It stays on the current tech's day too, and the points split evenly."
                            >
                                {t.first_name} {t.last_name}
                            </MenuItem>
                        ))}
                    </div>
                </>
            )}
            {quickActions?.onRemoveCrew && crewMembers(job).length > 0 && (
                <>
                    <MenuSeparator />
                    {crewMembers(job).map((m) => (
                        <MenuItem
                            key={m.userId}
                            onSelect={() => quickActions?.onRemoveCrew?.(job.id, m.userId)}
                            title={`${m.name} comes off this job only. ${job.assigned_to_name ?? "The lead"} keeps it, and the rest of ${m.firstName}'s day is unchanged.`}
                        >
                            {removeCrewLabel(m)}
                        </MenuItem>
                    ))}
                </>
            )}
            {(hasAdjustTime || hasMoveToSpecificDate || hasUnschedule) && (
                <>
                    <MenuSeparator />
                    {hasAdjustTime && (
                        <MenuItem onSelect={() => quickActions?.onAdjustTime?.(job.id)}>Adjust time</MenuItem>
                    )}
                    {hasMoveToSpecificDate && (
                        <MenuItem onSelect={() => quickActions?.onMoveToSpecificDate?.(job.id)}>
                            Move to specific date…
                        </MenuItem>
                    )}
                    {hasUnschedule && (
                        <MenuItem
                            onSelect={() => quickActions?.onUnschedule?.(job.id)}
                            title="Removes the time/tech but keeps the job in the dispatch backlog to schedule later."
                        >
                            Unschedule (back to backlog)
                        </MenuItem>
                    )}
                </>
            )}
            {hasMoveActions && (
                <>
                    <MenuSeparator />
                    {quickActions?.onMoveToTomorrow && (
                        <MenuItem onSelect={() => quickActions?.onMoveToTomorrow?.(job.id)}>
                            {quickActions?.moveToTomorrowLabel ?? "Move to tomorrow"}
                        </MenuItem>
                    )}
                    {quickActions?.onPushNextWeek && (
                        <MenuItem onSelect={() => quickActions?.onPushNextWeek?.(job.id)}>
                            {quickActions?.pushNextWeekLabel ?? "Push to next week"}
                        </MenuItem>
                    )}
                </>
            )}
            {hasMessageTech && (
                <>
                    <MenuSeparator />
                    <MenuItem onSelect={() => quickActions?.onMessageTech?.(job.id)}>Message tech</MenuItem>
                </>
            )}
            {hasSkip && (
                <>
                    <MenuSeparator />
                    <MenuItem
                        className="text-red-600 focus:text-red-600"
                        onSelect={() => quickActions?.onSkipJob?.(job.id)}
                        title="Marks the job as could-not-be-completed (e.g. no access). It leaves the schedule and goes to office review — it is not cancelled."
                    >
                        Mark cannot complete
                    </MenuItem>
                </>
            )}
            {hasCancel && (
                <>
                    {!hasSkip && <MenuSeparator />}
                    <MenuItem
                        className="text-red-600 focus:text-red-600"
                        onSelect={() => quickActions?.onCancelJob?.(job.id)}
                        title="Permanently cancels the job (requires a reason). Use this only when the work will not happen at all."
                    >
                        Cancel job
                    </MenuItem>
                </>
            )}
        </>
    );
}

/** Humanize a raw skill/credential code (e.g. "backflow_lead") into a short
 *  uppercase chip label (e.g. "BACKFLOW LEAD"). Codes are the only thing the
 *  dispatch skills summary exposes, so we format them for display here. */
function formatSkillCode(code: string): string {
    return code.replace(/[_-]+/g, " ").trim().toUpperCase();
}

/** Three-tier due pill (red overdue/<=7d, amber <=21d, gray else) — mirrors
 *  the backlog card logic so the popover and the backlog read the same. */
function popoverDueTier(dueByDate: string | null | undefined): "red" | "amber" | "gray" | null {
    if (!dueByDate) return null;
    const due = new Date(`${dueByDate}T00:00:00`);
    if (Number.isNaN(due.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due.getTime() - today.getTime()) / 86_400_000);
    if (diffDays <= 7) return "red";
    if (diffDays <= 21) return "amber";
    return "gray";
}

function popoverDueClass(tier: "red" | "amber" | "gray" | null): string {
    switch (tier) {
        case "red":
            return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
        case "amber":
            return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
        default:
            return "bg-foreground/10 text-foreground/70";
    }
}

function popoverDueLabel(dueByDate: string): string {
    const d = new Date(`${dueByDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return `Due ${dueByDate}`;
    return `Due ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

interface JobDetailHeader {
    site_name: string | null;
    company_name: string | null;
    site_address_line: string | null;
    site_address_line2: string | null;
    site_city: string | null;
    site_region: string | null;
    job_type_name: string | null;
    notes: string | null;
    assigned_to_name: string | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    due_by_date: string | null;
    // Vitals (2026-07-14): full row on the expanded popover.
    customer_po?: string | null;
    external_ref?: string | null;
    briefing_wo_number?: string | null;
    site_phone?: string | null;
    customer_phone?: string | null;
    site_water_account_number?: string | null;
    water_provider_is_portal?: boolean | null;
    water_provider_id?: string | null;
}

interface JobDetailResponse {
    job: JobDetailHeader;
    assets?: unknown[];
}

/**
 * On-demand job-detail popover shown on left-click of a scheduled block. Fetches
 * GET /api/jobs/:id (no change to the shared list query) and renders a skeleton
 * while loading. `fallback` carries the fields already on the block so we can
 * show device count / customer immediately and degrade gracefully on error.
 */
function JobDetailPopoverBody({
    jobId,
    fallback,
    onReschedule,
}: {
    jobId: string;
    fallback: DispatchJob;
    onReschedule: (() => void) | null;
}) {
    const detailQuery = useQuery({
        queryKey: ["job-detail", jobId],
        queryFn: () => api.get<JobDetailResponse>(`/api/jobs/${jobId}`),
        staleTime: 30_000,
    });

    const header = detailQuery.data?.job;
    const siteName = header?.site_name ?? fallback.site_label ?? fallback.title;
    const customer = header?.company_name ?? fallback.customer_name ?? null;
    // Full mailing address incl. ZIP (street + optional unit, city/region, ZIP).
    // Header has no postal, so the ZIP falls back to the block's own field.
    const streetWithUnit =
        [header?.site_address_line ?? fallback.site_address_line, header?.site_address_line2]
            .filter(Boolean)
            .join(", ") || null;
    const fullAddress = formatJobAddress({
        addressLine: streetWithUnit,
        city: header?.site_city ?? fallback.site_city,
        region: header?.site_region ?? fallback.site_region,
        postalCode: fallback.site_postal_code,
    });
    const jobType = header?.job_type_name ?? fallback.job_type_name ?? null;
    const deviceCount = detailQuery.data?.assets?.length ?? fallback.asset_count ?? 0;
    const assignedName = header?.assigned_to_name ?? fallback.assigned_to_name ?? null;
    const start = header?.scheduled_start ?? fallback.scheduled_start ?? null;
    const end = header?.scheduled_end ?? fallback.scheduled_end ?? null;
    const timeRange = start ? `${formatFieldTime(start)}${end ? ` – ${formatFieldTime(end)}` : ""}` : "Not scheduled";
    const notes = header?.notes ?? null;
    const dueByDate = header?.due_by_date ?? fallback.due_by_date ?? null;
    // Vitals (2026-07-14): the expanded view always shows the full row.
    const vitals = jobVitals({ ...fallback, ...(header ?? {}) });

    return (
        <div className="flex flex-col gap-2 text-xs">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="break-words text-sm font-semibold leading-tight text-foreground">{siteName}</div>
                    {customer && <div className="truncate text-foreground/70">{customer}</div>}
                </div>
                <PopoverClose
                    className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Close"
                >
                    <X className="h-4 w-4" />
                </PopoverClose>
            </div>

            {detailQuery.isLoading ? (
                <div className="flex items-center gap-2 py-3 text-foreground/60">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading details…
                </div>
            ) : (
                <>
                    {fullAddress && <div className="font-medium text-foreground/80">{fullAddress}</div>}
                    <div className="text-foreground/70">
                        {[jobType, deviceCount > 0 ? `${deviceCount} device${deviceCount === 1 ? "" : "s"}` : null]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                    </div>
                    <div className="text-foreground/70">
                        <span className="font-semibold">{assignedName ?? "Unassigned"}</span> · {timeRange}
                    </div>
                    {(vitals.refs.length > 0 || vitals.phone || vitals.acct.kind !== "none") && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-foreground/80">
                            {vitals.refs.map((r) => (
                                <span key={r} className="font-medium tabular-nums">
                                    {r}
                                </span>
                            ))}
                            {vitals.phone && (
                                <a href={`tel:${vitals.phone}`} className="tabular-nums text-primary hover:underline">
                                    {vitals.phone}
                                </a>
                            )}
                            {vitals.acct.kind === "value" && (
                                <span className="tabular-nums" title="Water provider account #">
                                    Acct {vitals.acct.value}
                                </span>
                            )}
                            {vitals.acct.kind === "paper" && (
                                <span
                                    className="text-muted-foreground"
                                    title="Paper-filing city — test reports are filed on paper, so there is no water-account number to track"
                                >
                                    no acct — paper
                                </span>
                            )}
                            {vitals.acct.kind === "missing" && (
                                <span className="rounded bg-amber-100 px-1 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                                    acct missing
                                </span>
                            )}
                        </div>
                    )}
                    {dueByDate && (
                        <span
                            className={cn(
                                "inline-flex w-fit items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                                popoverDueClass(popoverDueTier(dueByDate)),
                            )}
                            title={`Due ${dueByDate}`}
                        >
                            {popoverDueLabel(dueByDate)}
                        </span>
                    )}
                    <div>
                        <span className="font-semibold text-foreground/70">Notes</span>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-foreground/80">
                            {notes && notes.trim() ? notes : <span className="text-muted-foreground">No notes.</span>}
                        </p>
                    </div>
                </>
            )}

            <div className="flex items-center gap-2 border-t pt-2">
                {onReschedule && (
                    <Button size="sm" className="h-7 text-xs" onClick={onReschedule}>
                        Reschedule
                    </Button>
                )}
                {/* G5: same-tab; real href keeps cmd/ctrl+click for a new tab. */}
                <Link to={`/jobs/${jobId}`} className="text-xs font-medium text-primary hover:underline">
                    Open job →
                </Link>
            </div>
            <p className="text-[10px] text-muted-foreground">
                Reassign, cancel &amp; more: right-click the job or use the ⋮ button.
            </p>
        </div>
    );
}

// H11: grid blocks led with name + time but no status text, and en_route /
// office_review / held / arrived had little or misleading chrome. This gives
// each of those a short, accurate, color-coded label badge. Statuses that
// already read clearly from their icon (in_progress "now", completed check,
// cannot_complete alert) are intentionally omitted so the grid stays calm.
const GRID_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
    en_route: {
        label: "En route",
        cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200",
    },
    arrived: {
        label: "On site",
        cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200",
    },
    office_review: {
        label: "Office review",
        cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-200",
    },
    held: {
        label: "Held",
        cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-200",
    },
    pending_action: {
        label: "Action needed",
        cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-200",
    },
};

/** Accurate, human status label for the grid (distinct from the lifecycle
 *  stage label, which collapses en_route into "Scheduled"). */
function gridStatusLabel(status: string | null | undefined): string {
    const s = (status ?? "").toLowerCase().trim();
    const map: Record<string, string> = {
        draft: "Draft",
        awaiting_scheduling: "Awaiting scheduling",
        scheduled: "Scheduled",
        en_route: "En route",
        arrived: "On site",
        in_progress: "In progress",
        office_review: "Office review",
        cannot_complete: "Cannot complete",
        ready_for_invoice: "Ready for invoice",
        held: "Held",
        pending_action: "Action needed",
        completed: "Completed",
        closed: "Closed",
        cancelled: "Cancelled",
    };
    return map[s] ?? brandedLabel(status);
}

function JobBlock({
    block,
    techId,
    density = "comfortable",
    onJobClick,
    onJobEdit,
    onDragStart,
    onDragEnd,
    pulse,
    quickActions,
    requiredSkillCodes,
}: JobBlockProps) {
    const { job, leftPx, widthPx, minutes, clippedLeft, clippedRight, pushedMinutes } = block;
    const isCompact = density === "compact";
    // #628 item 1: a card packed off its clock pixel abuts the previous stop —
    // without a marker, a tight sequence reads as "close to the same time".
    // (Half-minute threshold dodges float noise; any real push is minutes.)
    const isPushed = pushedMinutes > 0.5;
    // Duration is carried honestly by a chip (width is capped, minutes aren't).
    const durationLabel = cardDurationLabel(minutes);
    // Work Inbox V2: ⚑ N corner tab, colored by the hottest task's heat.
    const taskFlag = useJobTaskFlag(job.id);
    // The Quiet Toolbar (10/10): marks from the job's own state (lib/job-marks.ts).
    // Water off already has its own red badge here, so it isn't repeated.
    const [quiet] = useQuietToolbar();
    const woMark = workOrderMissingMark(job);
    const marks = quiet
        ? jobMarks(job).filter((m) => m.label !== "Water off · waiting on NTE")
        : woMark
          ? [woMark]
          : [];
    const tradeHex = TRADE_HEX[job.trade_code ?? ""] ?? "#64748b";
    const startLabel = job.scheduled_start ? formatFieldTime(job.scheduled_start) : "";
    const endLabel = job.scheduled_end ? formatFieldTime(job.scheduled_end) : "";
    const isLocked = job.dispatch_lock_state === "confirmed";
    const dueState = classifyDueState(job.due_by_date, job.scheduled_start);
    const staleContact = isContactStale(job.site_contact_last_verified_at);
    // G3: repairs must stand out — accent border + wrench glyph + tinted
    // header (brighter, never dimmer) so a lane reads test-test-REPAIR-test
    // at a glance.
    const isRepair = isRepairIntentJob(job);
    const breakdown = job.points_breakdown ?? null;
    const ptsValue = job.computed_points == null ? null : Number(job.computed_points);
    const ptsLabel = ptsValue != null && Number.isFinite(ptsValue) ? formatPts(ptsValue) : null;

    // Lifecycle-derived visual treatment. The block leads with the customer
    // name; the trade is the left stripe; status is a subtle background tint
    // (not a badge). Terminal work fades hard so the eye lands on live/upcoming.
    const isDone = job.status === "completed" || job.status === "closed";
    const isOfficeReview = job.status === "office_review";
    const isCancelled = job.status === "cancelled";
    const isEnRoute = job.status === "en_route";
    const isInProgress = job.status === "in_progress";
    const isCannotComplete = job.status === "cannot_complete";
    // #1465 step 4 — "OK, show me": the card that moved out from under a
    // dispatcher glows where it now sits for a few seconds.
    const isHighlighted = useHighlightedJobId() === job.id;
    // Surface a tier badge only for the extremes (T1 highest, T3 lowest) —
    // T2 is the silent majority and would just add noise on the grid.
    const showTier = job.priority_tier === "T1" || job.priority_tier === "T3";
    // Pre-dispatch payment gate signal: card-required customer with no card
    // on file. These shouldn't normally be on the board (the gate blocks
    // scheduling) but legacy jobs can predate the profile change.
    const cardRequiredMissing =
        (job.payment_profile === "auto_charge" || job.payment_profile === "card_on_file_manual") &&
        !job.has_card_on_file;
    const riskActive = (pulse?.risk === "at_risk" || pulse?.risk === "overdue") && !isDone && !isCancelled;
    const riskRing =
        pulse?.risk === "overdue"
            ? "ring-2 ring-red-400 ring-offset-1 ring-offset-background"
            : pulse?.risk === "at_risk"
              ? "ring-2 ring-amber-400 ring-offset-1 ring-offset-background"
              : "";

    // Lead: customer name (the one thing readable at a glance). Falls back to
    // the job title when the customer is unknown. Secondary line carries the
    // job type (or the title / site when no job type exists).
    const lead = job.customer_name || job.title;
    const secondary = job.job_type_name || (job.customer_name ? job.title : (job.site_label ?? null));

    // Full mailing address (street, city/region, ZIP) — Corbin: the grid must
    // show the WHOLE address, large + bold, not just the jurisdiction. Truncates
    // on narrow blocks; the hover tooltip always carries the full string.
    const siteAddress = formatJobAddress({
        addressLine: job.site_address_line,
        city: job.site_city,
        region: job.site_region,
        postalCode: job.site_postal_code,
    });

    // City / jurisdiction chip: prefer the water provider (jurisdiction) name,
    // else the site city. Title spells out both so the short chip stays compact.
    const cityChip = job.water_provider_name || job.site_city || null;
    const cityChipTitle =
        [
            job.water_provider_name ? `Jurisdiction: ${job.water_provider_name}` : null,
            job.site_city ? `City: ${job.site_city}` : null,
        ]
            .filter(Boolean)
            .join("\n") || undefined;

    // Vitals row (2026-07-14): PO/WO reference + location phone + water
    // account on the card. Width-honest (G3): smallest blocks show only
    // WO + acct; the hover tooltip always carries the full row.
    const vitals = jobVitals(job);
    const vitalsShow = vitalsVisible(widthPx);
    const hasVitals = vitals.refs.length > 0 || !!vitals.phone || vitals.acct.kind !== "none";

    const tooltipLines: string[] = [job.title];
    if (job.customer_name) tooltipLines.push(`Customer: ${job.customer_name}`);
    if (vitals.refs.length > 0) tooltipLines.push(vitals.refs.join(" · "));
    if (vitals.phone) tooltipLines.push(`Phone: ${vitals.phone}`);
    if (vitals.acct.kind === "value") tooltipLines.push(`Water acct: ${vitals.acct.value}`);
    else if (vitals.acct.kind === "paper") tooltipLines.push("Water acct: none — paper city");
    else if (vitals.acct.kind === "missing") tooltipLines.push("Water acct: MISSING — portal city with no account #");
    if (job.site_label) tooltipLines.push(`Site: ${job.site_label}${job.site_city ? ` — ${job.site_city}` : ""}`);
    else if (job.site_city)
        tooltipLines.push(`Location: ${job.site_city}${job.site_region ? `, ${job.site_region}` : ""}`);
    if (siteAddress) tooltipLines.push(`Address: ${siteAddress}`);
    if (job.job_type_name) tooltipLines.push(`Type: ${job.job_type_name}`);
    if (job.trade_name) tooltipLines.push(`Trade: ${job.trade_name}`);
    if ((job.asset_count ?? 0) > 0)
        tooltipLines.push(
            `Assets: ${job.asset_count}${
                job.asset_types
                    ? ` (${job.asset_types
                          .split(",")
                          .map((t) => assemblyName(t))
                          .join(", ")})`
                    : ""
            }`,
        );
    // G3 trust tooltip: the calculator's reasoning, so a dispatcher can
    // always answer "why is this 35 points / 3 hours".
    if (ptsLabel != null)
        tooltipLines.push(
            `Points: ${ptsLabel}${breakdown?.points_explanation ? ` — ${breakdown.points_explanation}` : ""}`,
        );
    if (job.estimated_duration_minutes != null)
        tooltipLines.push(
            `Modeled time: ${formatBlockMinutes(job.estimated_duration_minutes)}${breakdown?.duration_explanation ? ` — ${breakdown.duration_explanation}` : ""}${job.scheduled_end ? " (manual schedule wins)" : ""}`,
        );
    if (startLabel) tooltipLines.push(`Time: ${startLabel}${endLabel ? ` – ${endLabel}` : ""}`);
    if (job.status) tooltipLines.push(`Status: ${gridStatusLabel(job.status)}`);
    if (isLocked) tooltipLines.push("Confirmed — auto-scheduler will not move");
    if (pulse && pulse.risk !== "ok") tooltipLines.push(pulse.note);

    // Hover kebab uses a controlled DropdownMenu; right-click / long-press
    // use Radix ContextMenu (native to those gestures, anchored at cursor).
    const [menuOpen, setMenuOpen] = useState(false);
    // Left-click opens an on-demand job-detail popover (the Schedule dialog
    // now only opens from the right-click "Schedule…" / "Reschedule" actions).
    const [popoverOpen, setPopoverOpen] = useState(false);
    const reassignTechs = (quickActions?.techs ?? []).filter((t) => t.id !== techId);
    const hasReassign = !!quickActions?.onReassign && reassignTechs.length > 0;
    const addCrewTechs = addCrewCandidates(job, quickActions?.techs ?? []);
    const hasAddCrew = !!quickActions?.onAddCrew && !!job.assigned_to && addCrewTechs.length > 0;
    const hasMoveActions = !!quickActions?.onMoveToTomorrow || !!quickActions?.onPushNextWeek;
    const hasAdjustTime = !!quickActions?.onAdjustTime;
    const hasSchedule = !!quickActions?.onSchedule;
    const hasMoveToSpecificDate = !!quickActions?.onMoveToSpecificDate;
    // Unschedule only makes sense once the job has a tech or a time on it.
    const hasUnschedule = !!quickActions?.onUnschedule && (!!job.assigned_to || !!job.scheduled_start);
    // "Message tech" is only meaningful once the job is sitting on a tech
    // row — the unassigned lane passes techId="" and so omits it gracefully.
    const hasMessageTech = !!quickActions?.onMessageTech && techId !== "";
    const hasSkip = !!quickActions?.onSkipJob;
    // Cancel is offered for any job that isn't already cancelled.
    const hasCancel = !!quickActions?.onCancelJob && job.status !== "cancelled";
    const crew = crewChip(job);

    const actionItemsProps = {
        job,
        lead,
        onJobClick,
        onJobEdit,
        hasSchedule,
        hasAdjustTime,
        hasMoveToSpecificDate,
        hasUnschedule,
        hasMessageTech,
        hasMoveActions,
        hasReassign,
        hasAddCrew,
        hasSkip,
        hasCancel,
        reassignTechs,
        addCrewTechs,
        quickActions,
    };

    const blockShellClass = cn(
        "group absolute top-1.5 bottom-1.5 cursor-grab overflow-hidden rounded-md border border-border text-left shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing",
        clippedLeft && "rounded-l-none",
        clippedRight && "rounded-r-none",
        riskRing,
        // Repair accent border (kept under the risk ring so overdue still wins).
        isRepair && !riskRing && "border-amber-500/80 shadow-[0_0_0_1px_rgba(245,158,11,0.35)]",
        isDone && "opacity-70",
        isOfficeReview && "opacity-90",
        isHighlighted && "job-card-glow",
        isCancelled && "opacity-70 grayscale line-through",
    );

    const blockShellStyle = {
        left: leftPx,
        width: widthPx,
        borderLeftWidth: 4,
        borderLeftColor: tradeHex,
        backgroundColor: `${tradeHex}1f`,
        ...(isInProgress ? { outline: `1.5px solid ${tradeHex}`, outlineOffset: "-1px" } : null),
    };

    return (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverAnchor asChild>
                <div
                    className={blockShellClass}
                    style={blockShellStyle}
                    data-job-id={job.id}
                    draggable
                    onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", job.id);
                        e.dataTransfer.effectAllowed = "move";
                        onDragStart?.(job.id);
                    }}
                    onDragEnd={() => onDragEnd?.()}
                >
                    <ContextMenu>
                        <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                                <ContextMenuTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Primary click opens the on-demand detail
                                            // popover. Rescheduling moves to the
                                            // right-click "Schedule…" item and the
                                            // popover's "Reschedule" button.
                                            setPopoverOpen(true);
                                        }}
                                        className="relative h-full w-full overflow-hidden text-left"
                                    >
                                        <JobTaskCornerTab flag={taskFlag} className="top-0.5" />
                                        {isLocked && (
                                            <span className="absolute left-1 top-1 z-10 inline-flex h-4 w-4 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm ring-1 ring-border">
                                                <Lock className="h-2.5 w-2.5" />
                                            </span>
                                        )}
                                        {onJobEdit && (
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                title="Edit job details"
                                                aria-label="Edit job details"
                                                onPointerDown={(e) => e.stopPropagation()}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onJobEdit(job.id);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onJobEdit(job.id);
                                                    }
                                                }}
                                                draggable={false}
                                                className={cn(
                                                    "absolute z-20 inline-flex h-4 w-4 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm ring-1 ring-border opacity-100 transition-opacity hover:bg-background focus:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100",
                                                    isLocked ? "left-6 top-1" : "left-1 top-1",
                                                )}
                                            >
                                                <Pencil className="h-2.5 w-2.5" />
                                            </span>
                                        )}

                                        {riskActive && (
                                            <span
                                                className={cn(
                                                    "absolute right-1 top-1 z-10 inline-block h-2 w-2 rounded-full ring-2 ring-card animate-pulse",
                                                    pulse?.risk === "overdue" ? "bg-red-500" : "bg-amber-400",
                                                )}
                                            />
                                        )}
                                        {isEnRoute && (
                                            <span className="absolute right-1 bottom-1 z-10 inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 shadow-sm animate-pulse">
                                                <Navigation className="h-2.5 w-2.5 text-white" />
                                            </span>
                                        )}
                                        {isInProgress && (
                                            <>
                                                <span
                                                    className="absolute left-1 bottom-1 z-10 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wide text-white shadow-sm"
                                                    style={{ backgroundColor: tradeHex }}
                                                >
                                                    now
                                                </span>
                                                <span className="absolute right-1 bottom-1 z-10 inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 shadow-sm ring-2 ring-amber-300/50">
                                                    <Wrench className="h-2.5 w-2.5 text-white" />
                                                </span>
                                            </>
                                        )}
                                        {isDone && (
                                            <span className="absolute right-1 top-1 z-10 inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-700 text-white shadow-sm">
                                                <CheckCircle className="h-2.5 w-2.5" />
                                            </span>
                                        )}
                                        {isCannotComplete && (
                                            <span className="absolute right-1 bottom-1 z-10 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 shadow-sm">
                                                <AlertTriangle className="h-2.5 w-2.5 text-white" />
                                            </span>
                                        )}

                                        <div className="relative flex h-full flex-col justify-between gap-0.5 p-1.5">
                                            <div
                                                className={cn(
                                                    "min-w-0 pr-5",
                                                    isLocked && "pl-5",
                                                    // Repair header tint — brighter, never dimmer (§6c).
                                                    isRepair && "-mx-1 -mt-1 rounded-t-[5px] bg-amber-400/25 px-1 pt-1",
                                                )}
                                            >
                                                <div className="flex min-w-0 items-center gap-1">
                                                    {isRepair && (
                                                        <span
                                                            className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-amber-700 text-white shadow-sm"
                                                            title={
                                                                // The work, as the tech reads it first (jobs.summary).
                                                                [
                                                                    "Repair / replace job",
                                                                    ...scopeLines(job.summary),
                                                                ].join("\n")
                                                            }
                                                        >
                                                            <Wrench className="h-2 w-2" />
                                                        </span>
                                                    )}
                                                    {showTier && (
                                                        <TierBadge
                                                            tier={job.priority_tier}
                                                            className="shrink-0 px-1 py-0"
                                                        />
                                                    )}
                                                    {cardRequiredMissing && (
                                                        <span
                                                            className="shrink-0 rounded px-1 py-0 text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200"
                                                            title="Card-required customer with no card on file"
                                                        >
                                                            No card
                                                        </span>
                                                    )}
                                                    <span className="truncate text-sm font-bold leading-tight text-foreground">
                                                        {lead}
                                                    </span>
                                                </div>
                                                {(secondary || durationLabel) && (
                                                    <div className="flex items-center gap-1 text-[10px] leading-tight text-foreground/70">
                                                        {secondary && (
                                                            <span className="min-w-0 truncate">{secondary}</span>
                                                        )}
                                                        <span
                                                            className="shrink-0 rounded bg-foreground/10 px-1 py-0 font-medium tabular-nums text-foreground/70"
                                                            title={`Scheduled ${durationLabel}`}
                                                        >
                                                            {durationLabel}
                                                        </span>
                                                    </div>
                                                )}
                                                {siteAddress && !isCompact && (
                                                    <div
                                                        className="mt-0.5 flex min-w-0 items-start gap-0.5 text-[11px] font-semibold leading-tight text-foreground"
                                                        title={siteAddress}
                                                    >
                                                        <MapPin className="mt-[1px] h-2.5 w-2.5 shrink-0 text-foreground/60" />
                                                        <span className="truncate">{siteAddress}</span>
                                                    </div>
                                                )}
                                                {GRID_STATUS_BADGE[job.status] && (
                                                    <span
                                                        className={cn(
                                                            "mt-0.5 inline-flex w-fit items-center rounded px-1 py-0 text-[9px] font-bold uppercase tracking-wide",
                                                            GRID_STATUS_BADGE[job.status].cls,
                                                        )}
                                                    >
                                                        {GRID_STATUS_BADGE[job.status].label}
                                                    </span>
                                                )}
                                                {job.fail_protocol_paused && (
                                                    <span
                                                        className="mt-0.5 inline-flex w-fit items-center rounded bg-red-600 px-1 py-0 text-[9px] font-bold uppercase tracking-wide text-white"
                                                        title="Jurisdiction fail protocol: the water is off at this site and we're waiting on the customer's approval to repair."
                                                    >
                                                        Water off — awaiting NTE
                                                    </span>
                                                )}
                                                {marks.length > 0 && (
                                                    <div
                                                        className="mt-0.5 flex flex-wrap gap-0.5"
                                                        data-testid="grid-card-marks"
                                                    >
                                                        {marks.map((m) => (
                                                            <Mark
                                                                key={m.label}
                                                                kind={m.kind}
                                                                label={m.label}
                                                                title={m.title}
                                                                className="px-1 py-0 text-[9px]"
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                                {(cityChip || (requiredSkillCodes?.length ?? 0) > 0) && (
                                                    <div className="mt-0.5 flex flex-wrap items-center gap-0.5">
                                                        {cityChip && (
                                                            <span
                                                                className="inline-flex items-center gap-0.5 rounded bg-slate-200/70 px-1 py-0 text-[9px] font-medium text-slate-700 dark:bg-slate-700/50 dark:text-slate-200"
                                                                title={cityChipTitle}
                                                            >
                                                                <MapPin className="h-2 w-2" />
                                                                <span className="max-w-[7rem] truncate">
                                                                    {cityChip}
                                                                </span>
                                                            </span>
                                                        )}
                                                        {(requiredSkillCodes ?? []).map((code) => (
                                                            <span
                                                                key={code}
                                                                className="rounded bg-indigo-100 px-1 py-0 text-[9px] font-medium tracking-wide text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200"
                                                                title={`Requires skill: ${formatSkillCode(code)}`}
                                                            >
                                                                {formatSkillCode(code)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {hasVitals && vitalsShow.refs && (
                                                <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0 text-[9px] leading-tight text-foreground/70">
                                                    {vitals.refs.map((r) => (
                                                        <span
                                                            key={r}
                                                            className="whitespace-nowrap font-medium tabular-nums"
                                                        >
                                                            {r}
                                                        </span>
                                                    ))}
                                                    {vitalsShow.phone && vitals.phone && (
                                                        <span className="whitespace-nowrap tabular-nums">
                                                            {vitals.phone}
                                                        </span>
                                                    )}
                                                    {vitalsShow.acct && vitals.acct.kind === "value" && (
                                                        <span
                                                            className="whitespace-nowrap tabular-nums"
                                                            title="Water provider account #"
                                                        >
                                                            Acct {vitals.acct.value}
                                                        </span>
                                                    )}
                                                    {vitalsShow.acct && vitals.acct.kind === "paper" && (
                                                        <span
                                                            className="whitespace-nowrap text-muted-foreground"
                                                            title="Paper-filing city — test reports are filed on paper, so there is no water-account number to track"
                                                        >
                                                            no acct — paper
                                                        </span>
                                                    )}
                                                    {vitalsShow.acct && vitals.acct.kind === "missing" && (
                                                        <span
                                                            className="whitespace-nowrap rounded bg-amber-100 px-1 font-bold uppercase text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
                                                            title="Portal city with no water account # on the site — fix before filing"
                                                        >
                                                            acct missing
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            <div className="flex min-w-0 items-center gap-1.5 text-[10px] text-foreground/70">
                                                {startLabel && (
                                                    /* #628 item 1: the start time is the disambiguator in a
                                                       tight sequence — bold on every card so a lane reads
                                                       "8:00 → 8:15 → 8:30", and packed cards lead with a →
                                                       marker + tint saying "back-to-back, drawn right of its
                                                       clock slot". */
                                                    <span
                                                        className={cn(
                                                            "inline-flex items-center gap-0.5 whitespace-nowrap font-semibold tabular-nums text-foreground",
                                                            isPushed && "rounded bg-foreground/10 px-1",
                                                        )}
                                                        title={
                                                            isPushed
                                                                ? `Back-to-back stop — starts at ${startLabel}. The card is drawn to the right of its clock slot so it stays readable; the time here is the real one.`
                                                                : undefined
                                                        }
                                                    >
                                                        {isPushed && <span aria-hidden>→</span>}
                                                        <Clock className="h-2.5 w-2.5" />
                                                        {startLabel}
                                                    </span>
                                                )}
                                                {(job.asset_count ?? 0) > 0 && (
                                                    <span
                                                        className="truncate"
                                                        title={
                                                            job.asset_types
                                                                ? job.asset_types
                                                                      .split(",")
                                                                      .map((t) => assemblyName(t))
                                                                      .join(", ")
                                                                : undefined
                                                        }
                                                    >
                                                        {formatAssets(job.asset_count, job.asset_types)}
                                                    </span>
                                                )}
                                                {ptsLabel != null && (
                                                    <span
                                                        className="inline-flex shrink-0 items-center rounded-full bg-primary/15 px-1.5 py-0 text-[11px] font-extrabold tabular-nums text-primary ring-1 ring-primary/25"
                                                        title={[
                                                            breakdown?.points_explanation
                                                                ? `Points: ${breakdown.points_explanation}`
                                                                : `Points: ${ptsLabel}`,
                                                            breakdown?.duration_explanation
                                                                ? `Time: ${breakdown.duration_explanation}`
                                                                : null,
                                                        ]
                                                            .filter(Boolean)
                                                            .join("\n")}
                                                    >
                                                        {ptsLabel} pts
                                                    </span>
                                                )}
                                                {crew && (
                                                    <span
                                                        className="inline-flex min-w-0 max-w-[180px] shrink-0 items-center gap-0.5 truncate rounded-full bg-violet-100 px-1 py-0 text-[9px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
                                                        title={`${crew.title} Dragging any copy moves it for all of them.`}
                                                        data-testid="crew-chip"
                                                    >
                                                        👥 {crew.count} · {crew.names}
                                                    </span>
                                                )}
                                                {staleContact && (
                                                    <span
                                                        className="ml-auto inline-flex shrink-0 items-center rounded-full bg-amber-100 px-1 py-0 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
                                                        title={
                                                            job.site_contact_last_verified_at
                                                                ? "Site contact not confirmed in over a year — verify before dispatching"
                                                                : "Site contact never verified — confirm before dispatching"
                                                        }
                                                    >
                                                        <UserX className="h-2.5 w-2.5" />
                                                    </span>
                                                )}
                                                {dueState && job.due_by_date && (
                                                    <span
                                                        className={cn(
                                                            "shrink-0 rounded-full px-1.5 py-0 text-[9px] font-semibold tabular-nums",
                                                            !staleContact && "ml-auto",
                                                            dueState === "overdue"
                                                                ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
                                                                : dueState === "scheduled_past_due"
                                                                  ? "bg-slate-200 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300"
                                                                  : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
                                                        )}
                                                        title={
                                                            dueState === "overdue"
                                                                ? "Past due"
                                                                : dueState === "scheduled_past_due"
                                                                  ? scheduledPastDueTitle(job.due_by_date)
                                                                  : `Due ${job.due_by_date}`
                                                        }
                                                    >
                                                        {formatDuePillLabel(job.due_by_date)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                </ContextMenuTrigger>
                            </TooltipTrigger>
                            {!popoverOpen && (
                                <TooltipContent
                                    side="bottom"
                                    sideOffset={4}
                                    className="max-w-xs text-xs whitespace-pre-line"
                                >
                                    {tooltipLines.join("\n")}
                                </TooltipContent>
                            )}
                        </Tooltip>
                        <ContextMenuContent className="w-52">
                            <JobActionItems
                                {...actionItemsProps}
                                MenuItem={ContextMenuItem}
                                MenuLabel={ContextMenuLabel}
                                MenuSeparator={ContextMenuSeparator}
                            />
                        </ContextMenuContent>
                    </ContextMenu>

                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                        <DropdownMenuTrigger asChild>
                            <span
                                role="button"
                                tabIndex={0}
                                title="Job actions"
                                aria-label="Job actions"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                draggable={false}
                                className="absolute right-1 top-1 z-30 inline-flex h-5 w-5 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm ring-1 ring-border opacity-100 transition-colors hover:bg-background focus:opacity-100 data-[state=open]:opacity-100"
                            >
                                <MoreVertical className="h-3 w-3" />
                            </span>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                            <JobActionItems
                                {...actionItemsProps}
                                MenuItem={DropdownMenuItem}
                                MenuLabel={DropdownMenuLabel}
                                MenuSeparator={DropdownMenuSeparator}
                            />
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </PopoverAnchor>
            <PopoverContent side="bottom" align="start" className="w-72">
                <JobDetailPopoverBody
                    jobId={job.id}
                    fallback={job}
                    onReschedule={
                        quickActions?.onSchedule
                            ? () => {
                                  setPopoverOpen(false);
                                  quickActions.onSchedule?.(job.id);
                              }
                            : null
                    }
                />
            </PopoverContent>
        </Popover>
    );
}
